/**
 * P4 (Juni 2026) – Betreiber-Konsole / Admin-Konsole (`/ops`).
 *
 * NUR für den Plattformbetreiber. Nicht in der normalen Navigation verlinkt und
 * vollständig getrennt vom Owner-/Employee-Login. Schutz: Eingabe des
 * OPS_API_TOKEN (nur sessionStorage, nie in URL/Logs). Die UI ist reiner Komfort
 * über die vorhandene, token-geschützte Ops-API – das Backend bleibt maßgeblich.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Ban,
  Building2,
  Coins,
  Cpu,
  ExternalLink,
  FileText,
  FileType2,
  FileUp,
  Globe,
  Info,
  KeyRound,
  Languages,
  LayoutDashboard,
  Link2,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  clearOpsToken,
  getOpsToken,
  opsApi,
  opsErrorMessage,
  setOpsToken,
} from "@/lib/opsApi";
import { PriceEditorSection } from "@/components/PriceEditorSection";
import { ShadowValidationSection } from "@/components/ShadowValidationSection";
import { OpsContractPreviewSection } from "@/components/OpsContractPreviewSection";
import { SalesCatalogSection } from "@/components/SalesCatalogSection";
// MOD-01b-4 (Betreiberfreigabe 13.10.): read-only Bereich „Abrechnung" im Mandanten-Detail.
import OpsAbrechnungSection from "./OpsAbrechnungSection";
import OpsVertragSection from "./OpsVertragSection"; // ZAHL-02c, Betreiberfreigabe 13.10.
import OpsOffeneVorgaengeSection from "./OpsOffeneVorgaengeSection"; // ZAHL-02e, Betreiberfreigabe 14.10.
import OpsSystemstartSection from "./OpsSystemstartSection"; // DEPLOY-01, Betreiberfreigabe 14.10.

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------

const STATUS_STYLE = {
  demo: "bg-sky-100 text-sky-800 border-sky-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-amber-100 text-amber-900 border-amber-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
};
const STATUS_LABEL = {
  demo: "Demo",
  active: "Aktiv",
  expired: "Abgelaufen",
  suspended: "Gesperrt",
};

// FIX-01b-2 (Betreiberfreigabe 12.10.): Klartext-Gründe des zentralen Zugriffszustands.
const ACCESS_REASON_LABEL = {
  read_only_expired: "Testphase abgelaufen",
  read_only_license_inaktiv: "offene Zahlung",
  read_only_suspended: "gesperrt",
  read_only_license_invalid: "Lizenz ungültig",
};

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <Badge
      variant="outline"
      data-testid={`ops-status-badge-${status}`}
      className={`rounded-md font-medium px-2.5 py-1 text-xs border ${style}`}
    >
      {STATUS_LABEL[status] || status || "?"}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("de-DE");
  } catch {
    return String(iso).slice(0, 10);
  }
}

function fmtDateTime(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

const LANG_LABEL = {
  de: "Deutsch",
  pl: "Polnisch",
  en: "Englisch",
  ro: "Rumänisch",
  uk: "Ukrainisch",
  bg: "Bulgarisch",
  tr: "Türkisch",
  hr: "Kroatisch",
  ru: "Russisch",
  ar: "Arabisch",
};
function langLabel(code) {
  return LANG_LABEL[code] || (code ? String(code).toUpperCase() : "–");
}

// MOD-01b-4 (Betreiberfreigabe 13.10., Soll 1): Kurzform der module_access-Quelle.
const MODUL_QUELLE_LABEL = {
  testphase: "Testphase",
  buchung: "Buchung",
  grundabo: "Grundabo",
  bestand: "Bestand",
  ausnahme: "Ausnahme",
};
function modulQuelleLabel(source) {
  return MODUL_QUELLE_LABEL[source] || (source ? String(source) : "");
}

// Zelle der Mandantenliste für ein Modul: „…" während des Ladens, „?" bei unbekannt/Fehler,
// sonst ✓ + Quelle bzw. –. Die Daten kommen nachträglich aus GET /ops/module-access
// (nicht aus tenant-metrics – OPS-504-FIX-Vertrag).
function ModulStatusZelle({ moduleAccess, tenantId, moduleId }) {
  if (moduleAccess.status === "laedt") {
    return <span className="text-slate-400 text-sm" title="wird geladen">…</span>;
  }
  const row = moduleAccess.byTenant[tenantId];
  const entry = row && row.status === "ok" ? row.module_access?.[moduleId] : null;
  if (!entry || typeof entry.active !== "boolean") {
    return <span className="text-slate-400 text-sm" title="unbekannt">?</span>;
  }
  if (!entry.active) return <span className="text-slate-400 text-sm">–</span>;
  return (
    <span className="text-emerald-600 text-sm whitespace-nowrap" title={modulQuelleLabel(entry.source)}>
      ✓{entry.source ? <span className="ml-1 text-xs text-slate-500">{modulQuelleLabel(entry.source)}</span> : null}
    </span>
  );
}

function fmtEuro(cents) {
  if (cents == null) return "–";
  return (
    (cents / 100).toLocaleString("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " €"
  );
}

function fmtNum(n) {
  if (n == null) return "–";
  try {
    return Number(n).toLocaleString("de-DE");
  } catch {
    return String(n);
  }
}

// P2.3b-1: µ€ (Mikro-Euro) → „12,50 €“. null/undefined → „kein Preis“.
function fmtEuroMicro(micro) {
  if (micro == null) return "kein Preis";
  return (
    (micro / 1_000_000).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

function shortId(tid) {
  if (!tid) return "–";
  return tid.length > 14 ? `${tid.slice(0, 10)}…` : tid;
}

function KpiCard({ label, value, testId, accent }) {
  return (
    <Card
      data-testid={testId}
      className="bg-white border border-slate-200 px-4 py-3 flex flex-col gap-1"
    >
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`font-heading text-2xl font-bold ${accent || "text-slate-900"}`}>
        {value}
      </span>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CLARTEXT · A1 (Feb 2026) — Aushangpflicht-Anzeige (Ops-Sektion)
// ---------------------------------------------------------------------------
// Verbindliche Zweistufigkeit:
//   1) „Auf Änderungen prüfen" → POST /ops/aushang/preview (kein Write)
//   2) „Übernehmen"            → POST /ops/aushang/apply (Hash-Vertrag + CAS)
// Der Übernehmen-Button bleibt so lange deaktiviert, wie mindestens ein
// Eintrag Status `nicht_pruefbar` trägt. Beide Buttons sind ausdrücklich
// getrennt (keine Kombination).
// ---------------------------------------------------------------------------
const AUSHANG_STATUS_STYLE = {
  unveraendert: "bg-slate-100 text-slate-600 border-slate-200",
  geaendert: "bg-amber-100 text-amber-900 border-amber-200",
  nicht_pruefbar: "bg-red-100 text-red-900 border-red-200",
};
const AUSHANG_STATUS_LABEL = {
  unveraendert: "unverändert",
  geaendert: "geändert",
  nicht_pruefbar: "nicht prüfbar",
};

function OpsAushangSection() {
  const [current, setCurrent] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [reason, setReason] = useState("");
  const [busyCheck, setBusyCheck] = useState(false);
  const [busyApply, setBusyApply] = useState(false);

  const refreshCurrent = useCallback(() => {
    opsApi
      .aushangCurrent()
      .then(setCurrent)
      .catch((e) => toast.error(opsErrorMessage(e)));
  }, []);

  useEffect(() => {
    refreshCurrent();
  }, [refreshCurrent]);

  const onCheck = async () => {
    setBusyCheck(true);
    try {
      const p = await opsApi.aushangPreview();
      setProposal(p);
      // A1-B Stufe 2 (Feb 2026): Erfolgsmeldung nur bei tatsächlich
      // erfolgreichem Quellabruf — nicht bei checked_successfully === 0.
      const checked = Number.isFinite(p?.checked_successfully) ? p.checked_successfully : null;
      const total = Number.isFinite(p?.total_sources)
        ? p.total_sources
        : (p?.eintraege?.length ?? 0);
      if (checked === 0) {
        toast.warning(
          `Vorschau geladen, aber 0 von ${total} Quellen erfolgreich prüfbar (nicht_pruefbar).`,
        );
      } else if (checked !== null && checked < total) {
        toast.warning(
          `Vorschau geladen: ${checked} von ${total} Quellen erfolgreich, ${total - checked} nicht prüfbar.`,
        );
      } else {
        toast.success("Vorschau erzeugt (kein Write).");
      }
    } catch (e) {
      toast.error(opsErrorMessage(e));
    } finally {
      setBusyCheck(false);
    }
  };

  const onApply = async () => {
    if (!proposal) return;
    setBusyApply(true);
    try {
      const res = await opsApi.aushangApply({
        expected_base_version_id: proposal.base_version_id,
        expected_proposal_hash: proposal.proposal_hash,
        proposal: proposal.eintraege,
        reason,
      });
      toast.success(`Übernommen. Neue Version: ${(res.version_id || "").slice(0, 8)}…`);
      setProposal(null);
      setReason("");
      refreshCurrent();
    } catch (e) {
      toast.error(opsErrorMessage(e));
    } finally {
      setBusyApply(false);
    }
  };

  const hasUncheckable = !!proposal?.eintraege?.some((e) => e.status === "nicht_pruefbar");
  const activeVersion = current?.version_id;
  const activeCount = (current?.eintraege || []).length;

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid="ops-aushang-section"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Aushangpflicht-Anzeige
          </h2>
          <p className="text-xs text-slate-500">
            Aktive Version: {activeVersion ? `${activeVersion.slice(0, 8)}… · ${activeCount} Einträge` : "noch nicht aktiviert"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCheck}
            disabled={busyCheck}
            data-testid="ops-aushang-check-button"
          >
            {busyCheck ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Auf Änderungen prüfen
          </Button>
          <Button
            size="sm"
            onClick={onApply}
            disabled={!proposal || hasUncheckable || busyApply}
            data-testid="ops-aushang-apply-button"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {busyApply ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Übernehmen
          </Button>
        </div>
      </div>
      {proposal ? (
        <div className="px-5 py-4 space-y-3" data-testid="ops-aushang-proposal">
          <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
            <span>Basis-Version: <code className="font-mono">{(proposal.base_version_id || "—").slice(0, 12)}</code></span>
            <span>Vorschlags-Hash: <code className="font-mono">{proposal.proposal_hash.slice(0, 16)}…</code></span>
            <span>{proposal.eintraege.length} Einträge</span>
          </div>
          {/* A1-B Stufe 2 (Feb 2026): transparente Ergebnisqualität. */}
          <div
            className="text-xs text-slate-600 flex items-center gap-3 flex-wrap bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
            data-testid="ops-aushang-summary"
          >
            <span data-testid="ops-aushang-summary-total">
              Quellen gesamt: <strong>{proposal.total_sources ?? proposal.eintraege.length}</strong>
            </span>
            <span data-testid="ops-aushang-summary-checked">
              erfolgreich geprüft: <strong>{proposal.checked_successfully ?? 0}</strong>
            </span>
            <span data-testid="ops-aushang-summary-unchanged">
              unverändert: <strong>{proposal.unchanged_count ?? 0}</strong>
            </span>
            <span data-testid="ops-aushang-summary-changed">
              geändert: <strong>{proposal.changed_count ?? 0}</strong>
            </span>
            <span data-testid="ops-aushang-summary-uncheckable">
              nicht prüfbar: <strong>{proposal.uncheckable_count ?? 0}</strong>
            </span>
            {Number.isFinite(proposal.duration_ms) && (
              <span data-testid="ops-aushang-summary-duration">
                Dauer: <strong>{Math.round(proposal.duration_ms) / 1000}s</strong>
              </span>
            )}
          </div>
          {(proposal.checked_successfully ?? 0) === 0 && (
            <div
              className="text-xs text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="ops-aushang-zero-check-hint"
            >
              Keine Quelle konnte erfolgreich abgerufen werden. Das Ergebnis ist
              unvollständig — eine Übernahme ist gesperrt. Ursache
              umgebungsabhängig (Egress-/Serverfilterung vermutet, nicht
              positiv bewiesen). Details je Quelle siehe Tabelle.
              {proposal.eintraege.some(
                (e) => typeof e.error_class === "string" &&
                       e.error_class.startsWith("unexpected_redirect_target"),
              ) && (
                <span
                  className="block mt-1"
                  data-testid="ops-aushang-unexpected-redirect-note"
                >
                  Hinweis: Einzelne Quellen wurden zwar per HTTP erreicht, die
                  finale URL nach Redirect adressiert aber nicht mehr denselben
                  fachlichen Inhalt (Fehlerklasse{" "}
                  <code className="font-mono">unexpected_redirect_target</code>).
                  Sie zählen deshalb NICHT als erfolgreich geprüft — zu
                  unterscheiden von reinen Netzwerk-Timeouts wie{" "}
                  <code className="font-mono">connect_timeout</code>.
                </span>
              )}
            </div>
          )}
          {hasUncheckable && (
            <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="ops-aushang-uncheckable-hint">
              Mindestens eine Quelle ist aktuell nicht prüfbar. Übernahme ist gesperrt, bis alle Quellen erreichbar sind.
            </div>
          )}
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Diagnose</TableHead>
                  <TableHead>Quelle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposal.eintraege.map((e, idx) => (
                  <TableRow key={`${e.kategorie}-${e.sort_order}-${idx}`} data-testid={`ops-aushang-row-${e.kategorie}-${e.sort_order}`}>
                    <TableCell className="text-sm">{e.titel}</TableCell>
                    <TableCell className="text-xs text-slate-500">{e.kategorie}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`rounded-md text-xs border ${AUSHANG_STATUS_STYLE[e.status] || "bg-slate-100"}`}
                        data-testid={`ops-aushang-status-${e.kategorie}-${e.sort_order}`}
                      >
                        {AUSHANG_STATUS_LABEL[e.status] || e.status}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="text-xs text-slate-500"
                      data-testid={`ops-aushang-diag-${e.kategorie}-${e.sort_order}`}
                    >
                      {e.http_status ? `HTTP ${e.http_status}` : "—"}
                      {Number.isFinite(e.duration_ms) ? ` · ${Math.round(e.duration_ms)}ms` : ""}
                      {e.error_class ? ` · ${e.error_class}` : ""}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 break-all max-w-xs">
                      <a href={e.display_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {e.display_url}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ops-aushang-reason" className="text-xs text-slate-500">
              Grund der Übernahme (optional, wird im ops_audit protokolliert)
            </Label>
            <Input
              id="ops-aushang-reason"
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              placeholder="z. B. Initialbefüllung A1"
              data-testid="ops-aushang-reason-input"
              className="h-9"
            />
          </div>
        </div>
      ) : (
        <div className="px-5 py-6 text-sm text-slate-500" data-testid="ops-aushang-no-proposal">
          Noch kein Vorschlag geladen. „Auf Änderungen prüfen" startet einen
          serverseitigen Diff gegen die aktiven Quell-URLs — keine Übernahme,
          kein Write.
        </div>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// CLARTEXT · Aushang 2.0 · A4 (2026-08-06) — Ops-Sektion „Aushangquellen"
// ---------------------------------------------------------------------------
// Reine Darstellung und Bedienung der A2-Endpunkte
// (`backend/routes/aushang_v2_ops.py`) — GET, PUT, DELETE, POST-bewusst-fehlend.
// KEIN Preview, KEIN Proposal, KEIN Apply, KEINE Backendänderung. Der
// Aushang-2.0-Zustand (30 Quellen) wird gelesen und in einer Tabelle
// dargestellt; die drei betreibergenehmigten UI-Aktionen (Upload,
// Discard, Bewusst fehlend) rufen ausschließlich die bereits vorhandenen
// Endpunkte auf und laden anschließend den Zustand neu.
// „Download der intern gespeicherten Datei" und „Neu validieren" sind
// mangels Backend-Endpunkt bewusst NICHT als Button vorhanden.
// ---------------------------------------------------------------------------

const A4_EXPECTED_SOURCE_COUNT = 30;

const A4_UPLOAD_STATUS_LABEL = {
  not_uploaded: "noch nicht hochgeladen",
  valid: "gültig",
  invalid: "ungültig",
  intentionally_missing: "bewusst fehlend",
  reference_only: "reiner Verweis",
};
const A4_UPLOAD_STATUS_STYLE = {
  not_uploaded: "bg-slate-100 text-slate-700 border-slate-200",
  valid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  invalid: "bg-red-100 text-red-800 border-red-200",
  intentionally_missing: "bg-amber-100 text-amber-900 border-amber-200",
  reference_only: "bg-sky-100 text-sky-800 border-sky-200",
};

const A4_VALIDATION_LABEL = {
  none: "—",
  pending: "prüft",
  valid: "geprüft: gültig",
  invalid: "geprüft: ungültig",
};

const A4_SOURCE_TYPE_LABEL = {
  xml: "XML",
  pdf: "PDF",
  link: "Link",
};

const A4_FILTER_OPTIONS = [
  { key: "all", label: "Alle" },
  { key: "xml", label: "XML" },
  { key: "pdf", label: "PDF" },
  { key: "link", label: "Link" },
  { key: "valid", label: "gültig" },
  { key: "invalid", label: "ungültig" },
  { key: "not_uploaded", label: "noch offen" },
  { key: "intentionally_missing", label: "bewusst fehlend" },
  { key: "reference_only", label: "reine Verweise" },
];

function a4FmtBytes(n) {
  if (n == null) return "—";
  const b = Number(n);
  if (!Number.isFinite(b) || b < 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function a4ShortHash(h) {
  if (!h) return "—";
  const s = String(h);
  return s.length <= 12 ? s : `${s.slice(0, 8)}…${s.slice(-4)}`;
}

function A4StatusBadge({ status }) {
  const style =
    A4_UPLOAD_STATUS_STYLE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = A4_UPLOAD_STATUS_LABEL[status] || status || "—";
  return (
    <Badge
      variant="outline"
      data-testid={`a4-upload-status-${status}`}
      className={`rounded-md font-medium px-2 py-0.5 text-xs border ${style}`}
    >
      {label}
    </Badge>
  );
}

function OpsAushangQuellenV2Section() {
  const [data, setData] = useState(null); // { entries, counters }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [rowBusy, setRowBusy] = useState({}); // { [source_identifier]: bool }
  const [discardTarget, setDiscardTarget] = useState(null); // slot | null
  const [missingDialog, setMissingDialog] = useState(null); // { slot, begruendung } | null
  // Aushang 2.0 · A5→A6 Verbindungsblock (Feb 2026):
  // stagingProposal hält das Backend-A5-Ergebnis 1:1 (KEINE
  // Client-Interpretation, KEINE eigene Hashberechnung).
  const [stagingProposal, setStagingProposal] = useState(null);
  const [busyStagingProposal, setBusyStagingProposal] = useState(false);
  const [busyStagingApply, setBusyStagingApply] = useState(false);
  const [applyReason, setApplyReason] = useState("");
  const [applyErrorDetail, setApplyErrorDetail] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError("");
    opsApi
      .aushangV2Quellen()
      .then((d) => setData(d))
      .catch((e) => setLoadError(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setRowBusyFor = (id, v) =>
    setRowBusy((prev) => {
      const next = { ...prev };
      if (v) next[id] = true;
      else delete next[id];
      return next;
    });

  const onUploadFile = async (slot, fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setRowBusyFor(slot.source_identifier, true);
    try {
      await opsApi.aushangV2Upload(slot.source_identifier, file);
      toast.success(`Datei für „${slot.title}" hochgeladen.`);
      refresh();
    } catch (e) {
      // Backendfehlerklasse sichtbar durchreichen (kein Erfolgs-Toast).
      const detail = e?.response?.data?.detail;
      const code =
        (detail && typeof detail === "object" && detail.code) || null;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        opsErrorMessage(e);
      toast.error(code ? `${code}: ${msg}` : msg);
    } finally {
      setRowBusyFor(slot.source_identifier, false);
    }
  };

  const onDiscard = async (slot) => {
    setRowBusyFor(slot.source_identifier, true);
    try {
      await opsApi.aushangV2Discard(slot.source_identifier);
      toast.success(`„${slot.title}" zurückgesetzt.`);
      refresh();
    } catch (e) {
      toast.error(opsErrorMessage(e));
    } finally {
      setRowBusyFor(slot.source_identifier, false);
      setDiscardTarget(null);
    }
  };

  const onSetMissing = async (slot, aktiv, begruendung) => {
    setRowBusyFor(slot.source_identifier, true);
    try {
      await opsApi.aushangV2SetIntentionallyMissing(
        slot.source_identifier,
        aktiv,
        aktiv ? begruendung : "",
      );
      toast.success(
        aktiv
          ? `„${slot.title}" als bewusst fehlend markiert.`
          : `„${slot.title}" ist nicht mehr bewusst fehlend.`,
      );
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code =
        (detail && typeof detail === "object" && detail.code) || null;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        opsErrorMessage(e);
      toast.error(code ? `${code}: ${msg}` : msg);
    } finally {
      setRowBusyFor(slot.source_identifier, false);
      setMissingDialog(null);
    }
  };

  // ------------------------------------------------------------------------
  // Aushang 2.0 · A5 (Änderungen ansehen) — ruft den bestehenden Backend-
  // Endpunkt POST /api/ops/aushang/staging/proposal auf. KEIN Legacy-
  // Preview, KEIN Apply, KEIN Write. Das Ergebnis (Einträge + Hashes +
  // Zähler) wird 1:1 aus dem Backend übernommen und im Dialog gerendert.
  // ------------------------------------------------------------------------
  const onFetchStagingProposal = async () => {
    setBusyStagingProposal(true);
    setApplyErrorDetail(null);
    try {
      const result = await opsApi.aushangV2StagingProposal();
      setStagingProposal(result);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code =
        (detail && typeof detail === "object" && detail.code) || null;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        opsErrorMessage(e);
      toast.error(code ? `${code}: ${msg}` : msg);
    } finally {
      setBusyStagingProposal(false);
    }
  };

  // ------------------------------------------------------------------------
  // Aushang 2.0 · A6 (Übernehmen) — ruft den bestehenden Backend-Endpunkt
  // POST /api/ops/aushang/apply mit den unveränderten Bindungswerten aus
  // dem A5-Proposal auf (expected_base_version_id, expected_proposal_hash,
  // expected_staging_state_hash, proposal, reason). Bei Erfolg: Dialog
  // schließen, Liste neu laden, Erfolgsmeldung. Bei Konflikt: Fehler
  // sichtbar, Proposal verwerfen (bei staging_state_changed) — kein
  // automatisches erneutes Apply.
  // ------------------------------------------------------------------------
  const onApplyStagingProposal = async () => {
    if (!stagingProposal) return;
    if (busyStagingApply) return; // Doppelklick-Schutz (Pflichttest 21).
    setBusyStagingApply(true);
    setApplyErrorDetail(null);
    try {
      const res = await opsApi.aushangApply({
        expected_base_version_id: stagingProposal.base_version_id,
        expected_proposal_hash: stagingProposal.proposal_hash,
        expected_staging_state_hash: stagingProposal.staging_state_hash,
        proposal: stagingProposal.eintraege,
        reason: applyReason,
      });
      toast.success(
        `Übernommen. Neue Version: ${(res.version_id || "").slice(0, 8)}…`,
      );
      setStagingProposal(null);
      setApplyReason("");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const code =
        (detail && typeof detail === "object" && detail.code) || null;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        opsErrorMessage(e);
      // Detailanzeige (verständliche Meldung für Betreiber, kein Retry).
      setApplyErrorDetail({
        code,
        message: msg,
        status,
        raw: detail && typeof detail === "object" ? detail : null,
      });
      // Bei staging_state_changed wird das alte Proposal verworfen (§ 5).
      if (code === "staging_state_changed") {
        setStagingProposal(null);
      }
      toast.error(code ? `${code}: ${msg}` : msg);
    } finally {
      setBusyStagingApply(false);
    }
  };

  const entries = data?.entries || [];
  const sortedEntries = [...entries].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  const counters = data?.counters || {};
  const searchLower = search.trim().toLowerCase();
  const filteredEntries = sortedEntries.filter((e) => {
    // Filter
    if (filter !== "all") {
      if (["xml", "pdf", "link"].includes(filter)) {
        if (e.source_type !== filter) return false;
      } else if (e.upload_status !== filter) {
        return false;
      }
    }
    // Suche (lokal, nur Anzeigefelder)
    if (searchLower) {
      const hay = [e.title, e.category, e.source_identifier]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    return true;
  });

  const totalCount = counters.gesamt ?? entries.length;
  const contractWarn =
    !loading && !loadError && entries.length !== A4_EXPECTED_SOURCE_COUNT;

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid="ops-aushang-quellen-section"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Aushangquellen
          </h2>
          <p className="text-xs text-slate-500">
            Manuelle 1-zu-1-Verwaltung der 30 amtlichen Quellen (Aushang 2.0).
            Kein Preview, kein Proposal, kein Apply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loading}
            data-testid="ops-aushang-quellen-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button
            size="sm"
            onClick={onFetchStagingProposal}
            disabled={busyStagingProposal}
            data-testid="ops-aushang-v2-proposal-button"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busyStagingProposal ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Änderungen ansehen
          </Button>
        </div>
      </div>

      {/* Fortschrittsübersicht (aus Backend-Countern, unabhängig vom Filter) */}
      <div
        className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1"
        data-testid="ops-aushang-quellen-progress"
      >
        <span data-testid="ops-aushang-quellen-progress-total">
          <strong>{totalCount}</strong> Quellen
        </span>
        <span data-testid="ops-aushang-quellen-progress-gueltig">
          <strong>{counters.gueltig ?? 0}</strong> gültig
        </span>
        <span data-testid="ops-aushang-quellen-progress-fehlerhaft">
          <strong>{counters.fehlerhaft ?? 0}</strong> ungültig
        </span>
        <span data-testid="ops-aushang-quellen-progress-ausstehend">
          <strong>{counters.ausstehend ?? 0}</strong> noch offen
        </span>
        <span data-testid="ops-aushang-quellen-progress-bewusst-fehlend">
          <strong>{counters.bewusst_fehlend ?? 0}</strong> bewusst fehlend
        </span>
        <span data-testid="ops-aushang-quellen-progress-reference-only">
          <strong>{counters.reference_only ?? 0}</strong> reine Verweise
        </span>
      </div>

      {/* Filter + Suche */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <div
          className="flex flex-wrap gap-1"
          role="tablist"
          aria-label="Aushangquellen-Filter"
          data-testid="ops-aushang-quellen-filter"
        >
          {A4_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setFilter(opt.key)}
              className={`px-2.5 py-1 text-xs rounded-md border ${
                filter === opt.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`ops-aushang-quellen-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Titel, Kategorie oder Identifier"
            className="h-8 pl-8 w-72 text-xs"
            data-testid="ops-aushang-quellen-search"
          />
        </div>
      </div>

      {contractWarn && (
        <div
          className="px-5 py-2 text-xs text-red-900 bg-red-50 border-b border-red-200"
          data-testid="ops-aushang-quellen-contract-warning"
        >
          Vertragswarnung: Es wurden {entries.length} Quellen geliefert — der
          Aushang-2.0-Vertrag verlangt genau {A4_EXPECTED_SOURCE_COUNT}. Bitte
          Ursache im Backend prüfen.
        </div>
      )}

      {loading ? (
        <div
          className="py-12 flex justify-center"
          data-testid="ops-aushang-quellen-loading"
        >
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : loadError ? (
        <div className="px-5 py-6" data-testid="ops-aushang-quellen-error">
          <p className="text-sm text-red-700 mb-3">{loadError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            data-testid="ops-aushang-quellen-retry"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Erneut versuchen
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="ops-aushang-quellen-table">
            <TableHeader>
              <TableRow>
                <TableHead>Quelle / Titel</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Identifier</TableHead>
                <TableHead>Dokumenttyp</TableHead>
                <TableHead>amtliche Quelle</TableHead>
                <TableHead>Upload-Status</TableHead>
                <TableHead>Validierung</TableHead>
                <TableHead>Dokumentversion</TableHead>
                <TableHead>Dokumentdatum</TableHead>
                <TableHead className="text-right">Größe</TableHead>
                <TableHead>Hash</TableHead>
                <TableHead>letzte Änderung</TableHead>
                <TableHead>Fehler</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={14}
                    className="text-center text-slate-400 py-6"
                    data-testid="ops-aushang-quellen-empty"
                  >
                    Keine Zeile entspricht Filter oder Suche.
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((slot) => {
                  const busy = !!rowBusy[slot.source_identifier];
                  const canUpload =
                    !slot.reference_only && slot.source_type !== "link";
                  const canDiscard =
                    !slot.reference_only &&
                    (slot.upload_status === "valid" ||
                      slot.upload_status === "invalid" ||
                      slot.upload_status === "intentionally_missing");
                  const canToggleMissing = !slot.reference_only;
                  const isMissing =
                    slot.upload_status === "intentionally_missing";
                  const accept =
                    slot.source_type === "pdf"
                      ? ".pdf"
                      : slot.source_type === "xml"
                        ? ".xml"
                        : undefined;
                  return (
                    <TableRow
                      key={slot.source_identifier}
                      data-testid={`ops-aushang-quellen-row-${slot.source_identifier}`}
                    >
                      <TableCell className="text-sm">
                        <div className="font-medium text-slate-800">
                          {slot.title || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {slot.category || "—"}
                      </TableCell>
                      <TableCell
                        className="text-xs font-mono text-slate-500"
                        data-testid={`ops-aushang-quellen-identifier-${slot.source_identifier}`}
                      >
                        {slot.source_identifier}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="inline-flex items-center gap-1">
                          {slot.source_type === "xml" ? (
                            <FileType2 className="w-3.5 h-3.5 text-slate-400" />
                          ) : slot.source_type === "pdf" ? (
                            <FileText className="w-3.5 h-3.5 text-slate-400" />
                          ) : slot.source_type === "link" ? (
                            <Link2 className="w-3.5 h-3.5 text-slate-400" />
                          ) : null}
                          <span>
                            {A4_SOURCE_TYPE_LABEL[slot.source_type] ||
                              slot.source_type ||
                              "—"}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {slot.display_url ? (
                          <a
                            href={slot.display_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-700 hover:underline inline-flex items-center gap-0.5"
                            data-testid={`ops-aushang-quellen-display-url-${slot.source_identifier}`}
                          >
                            öffnen
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <A4StatusBadge status={slot.upload_status} />
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {A4_VALIDATION_LABEL[slot.validation_status] ||
                          slot.validation_status ||
                          "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {slot.document_version || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {slot.document_date || "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs text-slate-600">
                        {a4FmtBytes(slot.staged_file_size)}
                      </TableCell>
                      <TableCell
                        className="text-xs font-mono text-slate-500"
                        title={slot.staged_file_hash || ""}
                        data-testid={`ops-aushang-quellen-hash-${slot.source_identifier}`}
                      >
                        {a4ShortHash(slot.staged_file_hash)}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {slot.staging_revision != null
                          ? `Rev. ${slot.staging_revision}`
                          : "—"}
                      </TableCell>
                      <TableCell
                        className="text-xs text-red-700 max-w-[220px] whitespace-normal"
                        data-testid={`ops-aushang-quellen-error-${slot.source_identifier}`}
                      >
                        {slot.error_class ? (
                          <>
                            <div className="font-medium">{slot.error_class}</div>
                            {slot.error_detail && (
                              <div className="text-[10px] text-red-600 mt-0.5">
                                {slot.error_detail}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <div className="inline-flex gap-1 items-center justify-end">
                          {canUpload && (
                            <label
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border cursor-pointer ${
                                busy
                                  ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                              }`}
                              data-testid={`ops-aushang-quellen-upload-label-${slot.source_identifier}`}
                            >
                              {busy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <FileUp className="w-3.5 h-3.5" />
                              )}
                              <span>hochladen</span>
                              <input
                                type="file"
                                className="hidden"
                                accept={accept}
                                disabled={busy}
                                onChange={(ev) => {
                                  onUploadFile(slot, ev.target.files);
                                  // Zurücksetzen, damit dieselbe Datei erneut
                                  // gewählt werden kann.
                                  ev.target.value = "";
                                }}
                                data-testid={`ops-aushang-quellen-upload-input-${slot.source_identifier}`}
                              />
                            </label>
                          )}
                          {canDiscard && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={busy}
                              onClick={() => setDiscardTarget(slot)}
                              data-testid={`ops-aushang-quellen-discard-${slot.source_identifier}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              zurücksetzen
                            </Button>
                          )}
                          {canToggleMissing && (
                            <Button
                              type="button"
                              variant={isMissing ? "outline" : "ghost"}
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={busy}
                              onClick={() => {
                                if (isMissing) {
                                  // Aufheben — kein Dialog nötig, kein Pflichtfeld.
                                  onSetMissing(slot, false, "");
                                } else {
                                  setMissingDialog({
                                    slot,
                                    begruendung: slot.begruendung || "",
                                  });
                                }
                              }}
                              data-testid={`ops-aushang-quellen-missing-${slot.source_identifier}`}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              {isMissing ? "aufheben" : "bewusst fehlend"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Aushang 2.0 · A5-Proposal-Dialog (Verbindungsblock A4→A5→A6) */}
      <Dialog
        open={!!stagingProposal}
        onOpenChange={(o) => {
          if (!o) {
            setStagingProposal(null);
            setApplyReason("");
            setApplyErrorDetail(null);
          }
        }}
      >
        <DialogContent
          className="max-w-5xl max-h-[88vh] overflow-y-auto"
          data-testid="ops-aushang-v2-proposal-dialog"
        >
          <DialogHeader>
            <DialogTitle>Aushang 2.0 — Änderungen ansehen</DialogTitle>
            <DialogDescription>
              Serverseitiges Proposal aus dem aktuellen Staging-Stand.
              Kein Write, kein Zeigerflip. &bdquo;Übernehmen&ldquo; bindet den Apply
              an die drei nachfolgend gezeigten Hashes.
            </DialogDescription>
          </DialogHeader>
          {stagingProposal && (
            <div className="space-y-3 py-2">
              <div
                className="text-xs text-slate-600 flex items-center gap-3 flex-wrap bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                data-testid="ops-aushang-v2-proposal-summary"
              >
                <span data-testid="ops-aushang-v2-proposal-count">
                  Einträge gesamt:{" "}
                  <strong>{stagingProposal.eintraege?.length ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-unveraendert">
                  unverändert:{" "}
                  <strong>{stagingProposal.zaehler?.unveraendert ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-geaendert">
                  geändert:{" "}
                  <strong>{stagingProposal.zaehler?.geaendert ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-nicht-pruefbar">
                  nicht prüfbar:{" "}
                  <strong>
                    {stagingProposal.zaehler?.nicht_pruefbar ?? 0}
                  </strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-gueltig">
                  gültig:{" "}
                  <strong>{stagingProposal.zaehler?.gueltig ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-ausstehend">
                  ausstehend:{" "}
                  <strong>{stagingProposal.zaehler?.ausstehend ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-fehlerhaft">
                  fehlerhaft:{" "}
                  <strong>{stagingProposal.zaehler?.fehlerhaft ?? 0}</strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-bewusst-fehlend">
                  bewusst fehlend:{" "}
                  <strong>
                    {stagingProposal.zaehler?.bewusst_fehlend ?? 0}
                  </strong>
                </span>
                <span data-testid="ops-aushang-v2-proposal-zaehler-reference-only">
                  reine Verweise:{" "}
                  <strong>
                    {stagingProposal.zaehler?.reference_only ?? 0}
                  </strong>
                </span>
              </div>

              <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span data-testid="ops-aushang-v2-base-version-id">
                  Basis-Version:{" "}
                  <code className="font-mono">
                    {stagingProposal.base_version_id
                      ? String(stagingProposal.base_version_id).slice(0, 12)
                      : "—"}
                  </code>
                </span>
                <span data-testid="ops-aushang-v2-proposal-hash">
                  Vorschlags-Hash:{" "}
                  <code className="font-mono">
                    {String(stagingProposal.proposal_hash || "").slice(0, 16)}
                    …
                  </code>
                </span>
                <span data-testid="ops-aushang-v2-staging-state-hash">
                  Staging-Hash:{" "}
                  <code className="font-mono">
                    {String(stagingProposal.staging_state_hash || "").slice(
                      0,
                      16,
                    )}
                    …
                  </code>
                </span>
              </div>

              <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md">
                <Table data-testid="ops-aushang-v2-proposal-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titel</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead>Identifier</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Marker</TableHead>
                      <TableHead>Content-Hash</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(stagingProposal.eintraege || []).map((entry, idx) => (
                      <TableRow
                        key={`${entry.source_identifier}-${idx}`}
                        data-testid={`ops-aushang-v2-proposal-row-${entry.source_identifier}`}
                      >
                        <TableCell className="text-sm">
                          {entry.titel}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {entry.kategorie}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">
                          {entry.source_identifier}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`rounded-md text-xs border ${
                              AUSHANG_STATUS_STYLE[entry.status] ||
                              "bg-slate-100"
                            }`}
                            data-testid={`ops-aushang-v2-proposal-status-${entry.source_identifier}`}
                          >
                            {AUSHANG_STATUS_LABEL[entry.status] || entry.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {entry.marker || "—"}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">
                          {a4ShortHash(entry.content_hash)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-1">
                <Label
                  htmlFor="ops-aushang-v2-apply-reason"
                  className="text-xs text-slate-500"
                >
                  Grund der Übernahme (optional, wird im ops_audit
                  protokolliert)
                </Label>
                <Input
                  id="ops-aushang-v2-apply-reason"
                  value={applyReason}
                  onChange={(ev) => setApplyReason(ev.target.value)}
                  placeholder="z. B. Turnusübernahme"
                  data-testid="ops-aushang-v2-apply-reason-input"
                  className="h-9"
                />
              </div>
            </div>
          )}
          {applyErrorDetail && (
            <div
              className="text-xs text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="ops-aushang-v2-apply-error"
            >
              <div className="font-semibold">
                {applyErrorDetail.code || `HTTP ${applyErrorDetail.status ?? "?"}`}
              </div>
              <div className="mt-0.5">{applyErrorDetail.message}</div>
              {applyErrorDetail.code === "staging_state_changed" && (
                <div className="mt-1">
                  Bitte &bdquo;Änderungen ansehen&ldquo; erneut ausführen &mdash; der
                  Staging-Stand hat sich verändert. Kein automatischer
                  zweiter Versuch.
                </div>
              )}
              {applyErrorDetail.raw?.unzulaessige_slots && (
                <div
                  className="mt-1"
                  data-testid="ops-aushang-v2-apply-error-inadmissible"
                >
                  Betroffene Quellen:{" "}
                  <code className="font-mono">
                    {applyErrorDetail.raw.unzulaessige_slots.join(", ")}
                  </code>
                </div>
              )}
              {applyErrorDetail.raw?.server_staging_state_hash && (
                <div
                  className="mt-1 text-[10px]"
                  data-testid="ops-aushang-v2-apply-error-server-hash"
                >
                  Server-Staging-Hash:{" "}
                  <code className="font-mono">
                    {String(
                      applyErrorDetail.raw.server_staging_state_hash,
                    ).slice(0, 16)}
                    …
                  </code>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStagingProposal(null);
                setApplyReason("");
                setApplyErrorDetail(null);
              }}
              disabled={busyStagingApply}
              data-testid="ops-aushang-v2-proposal-cancel"
            >
              Schließen
            </Button>
            <Button
              type="button"
              onClick={onApplyStagingProposal}
              disabled={busyStagingApply || !stagingProposal}
              data-testid="ops-aushang-v2-apply-button"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busyStagingApply ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : null}
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bestätigungsdialog Zurücksetzen */}
      <AlertDialog
        open={!!discardTarget}
        onOpenChange={(o) => {
          if (!o) setDiscardTarget(null);
        }}
      >
        <AlertDialogContent data-testid="ops-aushang-quellen-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Datei zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              {discardTarget?.title} · {discardTarget?.source_identifier}
              <br />
              Der Slot wird auf „noch nicht hochgeladen&quot; gesetzt. Bereits
              hochgeladene Dateiinhalte werden verworfen. Kein Preview, kein
              Apply.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ops-aushang-quellen-discard-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => discardTarget && onDiscard(discardTarget)}
              data-testid="ops-aushang-quellen-discard-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Zurücksetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: „bewusst fehlend" setzen (Begründung Pflicht) */}
      <Dialog
        open={!!missingDialog}
        onOpenChange={(o) => {
          if (!o) setMissingDialog(null);
        }}
      >
        <DialogContent data-testid="ops-aushang-quellen-missing-dialog">
          <DialogHeader>
            <DialogTitle>Bewusst fehlend markieren</DialogTitle>
            <DialogDescription>
              {missingDialog?.slot?.title} · {missingDialog?.slot?.source_identifier}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label
              htmlFor="ops-aushang-quellen-missing-begruendung"
              className="text-xs text-slate-500"
            >
              Begründung (Pflicht)
            </Label>
            <Input
              id="ops-aushang-quellen-missing-begruendung"
              value={missingDialog?.begruendung || ""}
              onChange={(ev) =>
                setMissingDialog((cur) =>
                  cur ? { ...cur, begruendung: ev.target.value } : cur,
                )
              }
              placeholder="z. B. Verordnung wurde durch nachfolgende Fassung ersetzt"
              data-testid="ops-aushang-quellen-missing-begruendung"
            />
            <p className="text-[11px] text-slate-500">
              Wird im ops_audit protokolliert. Kein Apply-Effekt in A4.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMissingDialog(null)}
              data-testid="ops-aushang-quellen-missing-cancel"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() =>
                missingDialog &&
                missingDialog.begruendung.trim() &&
                onSetMissing(missingDialog.slot, true, missingDialog.begruendung.trim())
              }
              disabled={!(missingDialog?.begruendung || "").trim()}
              data-testid="ops-aushang-quellen-missing-confirm"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Markieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}



// ---------------------------------------------------------------------------
// P9.3 – Tarifwechsel-Anfragen (read + Statuspflege OHNE Tarifwirkung).
// Setzt eine offene Anfrage auf approved/rejected – LÖST KEINEN Tarifwechsel
// aus (kein Stripe, kein Geldfluss, keine Änderung an subscription/limits).
// ---------------------------------------------------------------------------
const REQ_STATUS_STYLE = {
  pending: "bg-sky-100 text-sky-800 border-sky-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  canceled: "bg-slate-100 text-slate-600 border-slate-200",
};
const REQ_STATUS_LABEL = {
  pending: "Offen",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
  canceled: "Storniert",
};

function PlanChangeRequestsSection() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    opsApi
      .planChangeRequests()
      .then((d) => setRequests(d.requests || []))
      .catch((e) => toast.error(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await opsApi.setPlanChangeStatus(id, status, "");
      toast.success(status === "approved" ? "Anfrage freigegeben (ohne Tarifwechsel)." : "Anfrage abgelehnt.");
      load();
    } catch (e) {
      toast.error(opsErrorMessage(e));
    }
  };

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-plan-change-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Tarifwechsel-Anfragen</h2>
          <p className="text-xs text-slate-500">
            Freigabe/Ablehnung ist dokumentarisch – es erfolgt KEIN automatischer Tarifwechsel und keine Zahlung.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="ops-plan-change-refresh">
          <RefreshCw className="w-4 h-4 mr-1" />
          Aktualisieren
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Firma</TableHead>
              <TableHead>Von → Zu</TableHead>
              <TableHead>Intervall</TableHead>
              <TableHead className="text-right">MA b. Anfrage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Datum</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-400 py-6">Lade…</TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-400 py-6" data-testid="ops-plan-change-empty">
                  Keine Tarifwechsel-Anfragen.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((r) => (
                <TableRow key={r.id} data-testid={`ops-plan-change-row-${r.id}`}>
                  <TableCell className="text-sm">
                    <div className="font-medium text-slate-800">{r.company_name || r.tenant_id}</div>
                    <div className="text-xs text-slate-400">{r.company_code || shortId(r.tenant_id)}</div>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {(r.from_plan_name || r.from_plan)} → <strong>{r.requested_plan_name || r.requested_plan}</strong>
                  </TableCell>
                  <TableCell className="text-sm">{r.requested_interval || "–"}</TableCell>
                  <TableCell className="text-right text-sm">{r.active_employee_count_at_request ?? "–"}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`rounded-md text-xs border ${REQ_STATUS_STYLE[r.status] || "bg-slate-100"}`}
                      data-testid={`ops-plan-change-status-${r.id}`}
                    >
                      {REQ_STATUS_LABEL[r.status] || r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {r.status === "pending" ? (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "approved")} data-testid={`ops-plan-change-approve-${r.id}`}>
                          Freigeben
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "rejected")} data-testid={`ops-plan-change-reject-${r.id}`}>
                          Ablehnen
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">–</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Demo-Mandant anlegen (Formular + einmalige PIN-Anzeige)
// ---------------------------------------------------------------------------

function CreateTenantDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [days, setDays] = useState("14");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // enthält owner_pin – nur einmal!

  function reset() {
    setName("");
    setEmail("");
    setDays("14");
    setCode("");
    setBusy(false);
    setError("");
    setCreated(null);
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Firmenname ist Pflicht.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = { company_name: trimmed, account_status: "demo" };
      if (email.trim()) payload.email = email.trim();
      const d = Number(days);
      if (Number.isFinite(d) && d > 0) payload.trial_days = d;
      if (code.trim()) payload.company_code = code.trim();
      const res = await opsApi.createTenant(payload);
      setCreated(res); // owner_pin NUR hier im flüchtigen State – keine Speicherung
    } catch (err) {
      setError(opsErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function finishAndClose() {
    reset();
    onOpenChange(false);
    onCreated?.();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="ops-create-dialog">
        {!created ? (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Demo-Mandant anlegen</DialogTitle>
              <DialogDescription>
                Legt einen leeren Demo-Mandanten an. Die initiale Owner-PIN wird
                danach <strong>nur einmal</strong> angezeigt.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="ops-create-name">Firmenname *</Label>
                <Input
                  id="ops-create-name"
                  data-testid="ops-create-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z. B. Muster Bau GmbH"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ops-create-email">E-Mail (optional)</Label>
                <Input
                  id="ops-create-email"
                  data-testid="ops-create-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="kontakt@firma.de"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ops-create-days">Laufzeit (Tage)</Label>
                  <Input
                    id="ops-create-days"
                    data-testid="ops-create-days"
                    type="number"
                    min="1"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ops-create-code">Firmen-Code (optional)</Label>
                  <Input
                    id="ops-create-code"
                    data-testid="ops-create-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="automatisch"
                  />
                </div>
              </div>
              {error && (
                <p data-testid="ops-create-error" className="text-sm text-red-600">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="ops-create-submit"
                disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Anlegen
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div data-testid="ops-created-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                Mandant angelegt
              </DialogTitle>
              <DialogDescription>
                {created.company_name} · Code <strong>{created.company_code}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
                <p className="text-xs uppercase tracking-wide text-amber-800 font-semibold">
                  Initiale Owner-PIN
                </p>
                <p
                  data-testid="ops-created-pin"
                  className="font-heading text-4xl font-black tracking-[0.3em] text-slate-900 mt-1"
                >
                  {created.owner_pin}
                </p>
                <p className="text-sm text-amber-900 mt-2 font-medium">
                  ⚠ Diese PIN wird nur einmal angezeigt. Jetzt sicher notieren –
                  sie kann nicht erneut abgerufen werden.
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Der Mandant startet leer. Der Unternehmer ändert die PIN beim
                ersten Login und durchläuft den KI-Setup-Wizard.
              </p>
            </div>
            <DialogFooter>
              <Button
                data-testid="ops-created-done"
                onClick={finishAndClose}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Verstanden – schließen
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bestätigungs-Dialog (sperren / upgrade / verlängern / werksreset)
// ---------------------------------------------------------------------------

const ACTION_META = {
  suspend: { title: "Mandant sperren?", label: "Sperren", danger: true },
  upgrade: { title: "Zu zahlendem Kunden upgraden?", label: "Upgrade", danger: false },
  extend: { title: "Laufzeit verlängern", label: "Verlängern", danger: false },
  reset: { title: "Werksreset durchführen?", label: "Werksreset", danger: true },
};

function ConfirmDialog({ dlg, onClose, onConfirm, busy, resetText, setResetText, extendDays, setExtendDays }) {
  if (!dlg) return null;
  const meta = ACTION_META[dlg.type];
  const t = dlg.tenant;
  const disabled =
    busy ||
    (dlg.type === "reset" && resetText !== "WERKSRESET") ||
    (dlg.type === "extend" && !(Number(extendDays) > 0));

  return (
    <AlertDialog open={!!dlg} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent data-testid="ops-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{meta.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {t.company_name} · {t.company_code}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {dlg.type === "extend" && (
          <div className="space-y-1.5 py-2">
            <Label htmlFor="ops-extend-days">Verlängerung um (Tage)</Label>
            <Input
              id="ops-extend-days"
              data-testid="ops-extend-days-input"
              type="number"
              min="1"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
            />
          </div>
        )}

        {dlg.type === "upgrade" && (
          <p className="text-sm text-slate-600 py-1">
            Der Mandant wird auf <strong>active</strong> gesetzt, die Testfrist
            entfernt und Limits auf unbegrenzt. Fachdaten bleiben unverändert.
          </p>
        )}

        {dlg.type === "suspend" && (
          <p className="text-sm text-slate-600 py-1">
            Der Zugang wird gesperrt (read-only). Es werden keine Daten gelöscht;
            der Mandant kann jederzeit wieder aktiviert werden.
          </p>
        )}

        {dlg.type === "reset" && (
          <div className="space-y-2 py-2">
            <p className="text-sm text-red-700">
              Dies löscht alle Inhalte dieses <strong>Demo</strong>-Mandanten
              unwiderruflich. Zum Bestätigen <strong>WERKSRESET</strong> eintippen.
            </p>
            <Input
              data-testid="ops-reset-input"
              value={resetText}
              onChange={(e) => setResetText(e.target.value)}
              placeholder="WERKSRESET"
              autoComplete="off"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ops-confirm-cancel">Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            data-testid="ops-confirm-action"
            disabled={disabled}
            onClick={onConfirm}
            className={meta.danger ? "bg-red-600 hover:bg-red-700 text-white" : ""}
          >
            {meta.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Token-Maske
// ---------------------------------------------------------------------------

function TokenGate({ onSubmit, error, busy }) {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card
        className="w-full max-w-md bg-white border border-slate-200 shadow-sm p-8"
        data-testid="ops-token-form"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium">
          <Lock className="w-3.5 h-3.5" />
          Betreiber-Konsole
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-5">
          Anmeldung erforderlich
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Interner Bereich. Bitte den Betreiber-Token (OPS_API_TOKEN) eingeben.
        </p>
        <form
          className="mt-6 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ops-token">Betreiber-Token</Label>
            <Input
              id="ops-token"
              data-testid="ops-token-input"
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="••••••••••••"
              autoComplete="off"
            />
          </div>
          {error && (
            <p data-testid="ops-token-error" className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid="ops-token-submit"
            disabled={busy || !value.trim()}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Anmelden
          </Button>
        </form>
        <p className="mt-6 text-xs text-slate-400">
          Der Token wird nur in dieser Browser-Sitzung gehalten (kein dauerhafter
          Login). Bei Verlassen des Tabs ist eine erneute Eingabe nötig.
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// P2.1 – OPS Console V2: Mandanten-Detailansicht (Anzeige; einzige Schreib-
// aktion ist der Owner-PIN-Reset in Bereich 6, 2026-09).
// Lädt /ops/tenants/{id}/detail und zeigt 5 Anzeige-Bereiche. Fehlende Daten
// erscheinen als „keine Daten“ – es wird nichts erfunden.
// ---------------------------------------------------------------------------

const TRANSLATION_BUCKETS = [
  ["complete", "text-emerald-700"],
  ["partial", "text-sky-700"],
  ["pending", "text-slate-500"],
  ["processing", "text-indigo-700"],
  ["error", "text-red-600"],
  ["stale", "text-amber-700"],
];

function DetailSection({ title, icon: Icon, count, testId, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <h3 className="font-heading text-sm font-semibold text-slate-800">{title}</h3>
        {count != null && <span className="text-xs text-slate-400">({count})</span>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm border-b border-slate-50 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium text-right" data-testid={testId}>
        {value == null || value === "" ? "–" : value}
      </span>
    </div>
  );
}

function NoData({ testId }) {
  return (
    <p className="text-sm text-slate-400 italic" data-testid={testId}>
      keine Daten
    </p>
  );
}

// ---------------------------------------------------------------------------
// Owner-PIN-Reset (2026-09): zweiphasiger Dialog in der Mandanten-Detailansicht.
// Phase 1: Firmencode startet LEER und muss aktiv eingetippt werden
// (confirm_company_code-Schutz des Backends). Phase 2: der neue PIN wird
// GENAU EINMAL angezeigt; Schließen verwirft PIN + Eingabe vollständig.
// ---------------------------------------------------------------------------

function OwnerPinResetDialog({ open, onClose, tenantId, companyName }) {
  const [confirmCode, setConfirmCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  function handleClose() {
    // PIN + eingegebener Firmencode vollständig aus dem UI-Zustand entfernen.
    setConfirmCode("");
    setError("");
    setResult(null);
    setBusy(false);
    onClose();
  }

  async function doReset() {
    if (busy || !confirmCode.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await opsApi.resetOwnerPin(tenantId, confirmCode.trim());
      setResult(res);
      setConfirmCode("");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) setError("Firmencode stimmt nicht überein.");
      else if (status === 404) setError("Mandant nicht gefunden.");
      else setError(opsErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyPin() {
    try {
      await navigator.clipboard.writeText(result.owner_pin);
      toast.success("PIN in die Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich — PIN bitte manuell übernehmen.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="ops-pin-reset-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle>Owner-PIN zurücksetzen?</DialogTitle>
              <DialogDescription>{companyName || "Mandant"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-1">
              <p className="text-sm text-red-700">
                Der alte PIN wird sofort ungültig, alle Owner-Sitzungen werden
                abgemeldet. Zum Bestätigen den <strong>Firmencode</strong> des
                Mandanten eintippen.
              </p>
              <Label htmlFor="ops-pin-reset-code">Firmencode</Label>
              <Input
                id="ops-pin-reset-code"
                data-testid="ops-pin-reset-code-input"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="Firmencode exakt eintippen"
                autoComplete="off"
              />
              {error && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1.5"
                  data-testid="ops-pin-reset-error"
                >
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="ops-pin-reset-cancel">
                Abbrechen
              </Button>
              <Button
                data-testid="ops-pin-reset-submit"
                disabled={busy || !confirmCode.trim()}
                onClick={doReset}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                PIN zurücksetzen
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Neuer Owner-PIN</DialogTitle>
              <DialogDescription>
                {result.company_name || companyName} · {result.company_code}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="flex items-center justify-center gap-3 rounded-lg border border-slate-200 bg-slate-50 py-5">
                <span
                  className="font-mono text-3xl font-bold tracking-[0.3em] text-slate-900"
                  data-testid="ops-pin-reset-new-pin"
                >
                  {result.owner_pin}
                </span>
                <Button size="sm" variant="outline" onClick={copyPin} data-testid="ops-pin-reset-copy">
                  Kopieren
                </Button>
              </div>
              <p
                className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                data-testid="ops-pin-reset-warning"
              >
                Dieser PIN ist <strong>nur jetzt einmalig sichtbar</strong> —
                sicher an den Kunden weitergeben, nicht speichern. Beim nächsten
                Login muss der Kunde einen eigenen PIN wählen.
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={handleClose}
                data-testid="ops-pin-reset-done"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Schließen (PIN verwerfen)
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sprach-Entitlement (Juni 2026): Verwaltungs-UI vor dem BESTEHENDEN Endpoint
// POST /ops/tenants/{id}/language-entitlement (ersetzt die Override-Liste
// komplett; das Backend reiht für NEU hinzugefügte Sprachen einen KOSTEN-
// pflichtigen Bestands-Backfill über alle released Welten ein — Entfernen
// löst KEINEN Backfill aus). Fehlbedien-Schutz: Firmencode-Bestätigung
// CLIENTSEITIG (der Endpoint kennt kein confirm-Feld und bleibt unberührt).
// ---------------------------------------------------------------------------

const ENTITLEMENT_LANGS = ["en", "pl", "ro", "uk", "bg", "tr", "hr", "ru"];

function LanguageEntitlementConfirmDialog({
  open, onClose, tenantId, companyCode, companyName,
  added, removed, newList, multilanguageIncluded, releasedWorldCount, onSaved,
}) {
  const [confirmCode, setConfirmCode] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function handleClose() {
    setConfirmCode("");
    setReason("");
    setError("");
    setBusy(false);
    onClose();
  }

  async function doSave() {
    if (busy || !confirmCode.trim() || !reason.trim()) return;
    // Clientseitiger Fehlbedien-Schutz gegen den falschen Mandanten (Muster
    // PIN-Reset; hier ohne Server-Feld — Betreiber-Freigabe Punkt 5).
    if (confirmCode.trim().toLowerCase() !== (companyCode || "").trim().toLowerCase()) {
      setError("Firmencode stimmt nicht überein.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await opsApi.setLanguageEntitlement(tenantId, {
        entitled_languages: newList,
        multilanguage_included: multilanguageIncluded,
        reason: reason.trim(),
        billed: false,
      });
      // MOD-01b-4 (Betreiberfreigabe 13.10., D3): nennt, für welche neu gebuchten Sprachen
      // Übersetzungen angestoßen wurden (nur Sprachen zählbarer Mitarbeiter).
      const backfill = Array.isArray(res?.backfill_languages) ? res.backfill_languages : [];
      if (added.length === 0) {
        toast.success("Gebuchte Sprachen gespeichert.");
      } else if (backfill.length > 0) {
        toast.success(
          `Gebuchte Sprachen gespeichert. Übersetzungen angestoßen für: ${backfill.map(langLabel).join(", ")}.`,
        );
      } else {
        toast.success(
          "Gebuchte Sprachen gespeichert. Keine Übersetzungen angestoßen (kein Mitarbeiter mit dieser Sprache).",
        );
      }
      onSaved(res);
      handleClose();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setError("Mandant nicht gefunden.");
      else setError(opsErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="ops-lang-dialog">
        <DialogHeader>
          <DialogTitle>Gebuchte Sprachen ändern?</DialogTitle>
          <DialogDescription>{companyName || "Mandant"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {added.length > 0 && (
            <p className="text-sm text-slate-700" data-testid="ops-lang-added-list">
              <strong>Neu hinzu:</strong> {added.map(langLabel).join(", ")}
            </p>
          )}
          {removed.length > 0 && (
            <p className="text-sm text-slate-700" data-testid="ops-lang-removed-list">
              <strong>Entfernt:</strong> {removed.map(langLabel).join(", ")}
            </p>
          )}
          {added.length > 0 && (
            <p
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
              data-testid="ops-lang-cost-hint"
            >
              <strong>Kostenpflichtiger Bestands-Backfill:</strong>{" "}
              {releasedWorldCount} freigegebene Welt(en) × {added.length} neue
              Sprache(n) = bis zu{" "}
              <strong>{releasedWorldCount * added.length} Übersetzungs-Jobs</strong>{" "}
              werden nach dem Speichern eingereiht (bereits vollständig
              übersetzte Welten werden übersprungen). Eingereiht wird nur für
              Sprachen, die zählbare Mitarbeiter eingestellt haben.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="ops-lang-reason">Grund (Pflicht, geht ins Audit)</Label>
            <Input
              id="ops-lang-reason"
              data-testid="ops-lang-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Kunde hat Sprachpaket gebucht"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ops-lang-code">Firmencode zur Bestätigung</Label>
            <Input
              id="ops-lang-code"
              data-testid="ops-lang-confirm-code-input"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="Firmencode exakt eintippen"
              autoComplete="off"
            />
          </div>
          {error && (
            <p
              className="text-sm text-red-600 flex items-center gap-1.5"
              data-testid="ops-lang-error"
            >
              <AlertTriangle className="w-4 h-4" />
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="ops-lang-cancel">
            Abbrechen
          </Button>
          <Button
            data-testid="ops-lang-submit"
            disabled={busy || !confirmCode.trim() || !reason.trim()}
            onClick={doSave}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Änderungen speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LanguageEntitlementSection({ detail, tenantId, onEntitlementSaved }) {
  const ent = detail.language_entitlement || {};
  const currentKey = ENTITLEMENT_LANGS
    .filter((l) => (ent.entitled_languages || []).includes(l))
    .join(",");
  const current = currentKey ? currentKey.split(",") : [];
  const [selected, setSelected] = useState(current);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Bei neuem Detail-Load / nach dem Speichern die Auswahl neu spiegeln.
  useEffect(() => {
    setSelected(currentKey ? currentKey.split(",") : []);
  }, [currentKey, tenantId]);

  const added = ENTITLEMENT_LANGS.filter((l) => selected.includes(l) && !current.includes(l));
  const removed = ENTITLEMENT_LANGS.filter((l) => current.includes(l) && !selected.includes(l));
  const dirty = added.length > 0 || removed.length > 0;
  const releasedWorldCount = (detail.worlds || []).filter((w) => w.released).length;
  const newList = ENTITLEMENT_LANGS.filter((l) => selected.includes(l));
  // Betreiber-Freigabe Punkt 3: Ist-Wert beibehalten; nur wenn er fehlt/false
  // UND Sprachen gebucht werden, auf true heben (sonst wirkungslose Buchung).
  const multilanguageIncluded = ent.multilanguage_included === true || newList.length > 0;

  function toggle(code) {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  return (
    <DetailSection title="Sprachen" icon={Languages} testId="ops-detail-languages">
      <p className="text-sm text-slate-600 mb-3">
        Gebuchte Mitarbeiter-Sprachen dieses Mandanten. Deutsch ist immer
        verfügbar und wird nicht gebucht. Das <strong>Hinzufügen</strong> einer
        Sprache löst einen kostenpflichtigen Bestands-Backfill über alle
        freigegebenen Welten aus — Bestätigung mit Firmencode erforderlich.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ENTITLEMENT_LANGS.map((code) => (
          <label
            key={code}
            className="flex items-center gap-2 text-sm rounded-md border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50"
            data-testid={`ops-lang-option-${code}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 accent-slate-900"
              checked={selected.includes(code)}
              onChange={() => toggle(code)}
              data-testid={`ops-lang-checkbox-${code}`}
            />
            <span>{langLabel(code)}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 mt-3 flex-wrap">
        <p className="text-xs text-slate-400" data-testid="ops-lang-diff-summary">
          {dirty
            ? `${added.length} hinzugefügt · ${removed.length} entfernt (noch nicht gespeichert)`
            : "Keine Änderungen."}
        </p>
        <Button
          variant="outline"
          disabled={!dirty}
          onClick={() => setConfirmOpen(true)}
          data-testid="ops-lang-save-open"
        >
          Änderungen speichern…
        </Button>
      </div>
      <LanguageEntitlementConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        tenantId={tenantId}
        companyCode={detail.info?.company_code}
        companyName={detail.info?.company_name}
        added={added}
        removed={removed}
        newList={newList}
        multilanguageIncluded={multilanguageIncluded}
        releasedWorldCount={releasedWorldCount}
        onSaved={onEntitlementSaved}
      />
    </DetailSection>
  );
}

// ---------------------------------------------------------------------------
// ZAHL-01 (W1, Juni 2026) – Abrechnungs-Nachbearbeitung je Mandant.
// Read-only-Anzeige der offenen/erledigten Einträge (fehlgeschlagene
// Subscription-Kündigungen bzw. Dubletten) PLUS die Betreiber-Aktion
// „Kündigung erneut versuchen" (idempotent; bereits gekündigt gilt als Erfolg →
// Eintrag erledigt). Gleiche Zugriffsstufe wie alle Ops-Aktionen (OPS_API_TOKEN
// via opsApi). KEINE Mutation außer dem bestehenden Retry-Endpunkt.
// ---------------------------------------------------------------------------
const FOLLOWUP_ACTION_LABEL = {
  cancel_subscription: "Kündigung",
  duplicate_subscription: "Dublette",
};

function BillingFollowupsSection({ tenantId }) {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryingId, setRetryingId] = useState("");

  const load = useCallback(() => {
    if (!tenantId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    opsApi
      .billingFollowups(tenantId)
      .then((d) => {
        if (active) setFollowups(d?.followups || []);
      })
      .catch((e) => {
        if (active) setError(opsErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId]);

  useEffect(() => load(), [load]);

  const handleRetry = async (f) => {
    setRetryingId(f.id);
    try {
      const res = await opsApi.billingFollowupRetry(f.id);
      const done =
        res?.followup?.status === "erledigt" || res?.retry?.cancelled === true;
      if (done) toast.success("Kündigung erfolgreich – Eintrag erledigt.");
      else toast.warning("Kündigung weiterhin offen – bitte erneut versuchen.");
      load();
    } catch (e) {
      toast.error(opsErrorMessage(e));
    } finally {
      setRetryingId("");
    }
  };

  const openCount = followups.filter((f) => f.status === "offen").length;

  return (
    <DetailSection
      title="Abrechnung · Nachbearbeitung"
      icon={Coins}
      count={openCount || null}
      testId="ops-detail-billing-followups"
    >
      {loading ? (
        <div className="py-4 flex justify-center" data-testid="ops-followups-loading">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600" data-testid="ops-followups-error">
          {error}
        </p>
      ) : followups.length === 0 ? (
        <NoData testId="ops-detail-billing-followups-empty" />
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="ops-followups-table">
            <TableHeader>
              <TableRow>
                <TableHead>Aktion</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Versuche</TableHead>
                <TableHead>Letzter Fehler / Hinweis</TableHead>
                <TableHead>Aktualisiert</TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {followups.map((f) => (
                <TableRow key={f.id} data-testid={`ops-followup-${f.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">
                    {FOLLOWUP_ACTION_LABEL[f.action] || f.action}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        f.status === "erledigt"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }
                      data-testid={`ops-followup-status-${f.id}`}
                    >
                      {f.status === "erledigt" ? "erledigt" : "offen"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm text-slate-600">
                    {f.attempts ?? 0}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs">
                    <span className="block truncate" title={f.last_error || ""}>
                      {f.last_error || "–"}
                    </span>
                    {f.note && (
                      <span
                        className="block text-xs text-amber-700"
                        data-testid={`ops-followup-note-${f.id}`}
                      >
                        {f.note}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {fmtDateTime(f.updated_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {f.status === "offen" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-200"
                        disabled={retryingId === f.id}
                        onClick={() => handleRetry(f)}
                        data-testid={`ops-followup-retry-${f.id}`}
                      >
                        {retryingId === f.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-1" />
                        )}
                        Kündigung erneut versuchen
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">–</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </DetailSection>
  );
}

// WELT-02b (Betreiberfreigabe 12.10.) – read-only Welten-Paket-Übersicht im
// Mandanten-Detail: Zusatzkontingent (extra) + Paketbuchungen. Keine Bearbeitung.
const WELTEN_PAKET_STATUS_LABEL = {
  pending: "ausstehend",
  gutgeschrieben: "gutgeschrieben",
  zurueckgebucht: "zurückgebucht",
  fehlgeschlagen: "fehlgeschlagen",
};

function fmtEuroNet(cents) {
  if (cents == null || Number.isNaN(Number(cents))) return "–";
  return `${(Number(cents) / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} € netto`;
}

function WeltenPaketSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return undefined;
    // Guard analog Unterweisungen/getWorldQuota: hält bestehende Tests grün, die
    // opsApi nur partiell mocken (ohne tenantWeltenPaket). Produktiv immer gesetzt.
    if (typeof opsApi.tenantWeltenPaket !== "function") return undefined;
    let active = true;
    setLoading(true);
    setError("");
    opsApi
      .tenantWeltenPaket(tenantId)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setError(opsErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId]);

  const bookings = data?.bookings || [];

  return (
    <DetailSection
      title="Welten-Pakete"
      icon={Globe}
      count={bookings.length || null}
      testId="ops-detail-welten-paket"
    >
      <InfoRow
        label="Zusatzkontingent (extra)"
        value={data ? `${Number(data.extra ?? 0)} Welten` : "–"}
        testId="ops-detail-welten-paket-extra"
      />
      {loading ? (
        <div className="py-4 flex justify-center" data-testid="ops-welten-paket-loading">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600 mt-2" data-testid="ops-welten-paket-error">
          {error}
        </p>
      ) : bookings.length === 0 ? (
        <div className="mt-2">
          <NoData testId="ops-detail-welten-paket-empty" />
        </div>
      ) : (
        <div className="overflow-x-auto mt-2">
          <Table data-testid="ops-welten-paket-table">
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Session-ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((b) => (
                <TableRow
                  key={b.stripe_session_id}
                  data-testid={`ops-welten-paket-row-${b.stripe_session_id}`}
                >
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                    {fmtDate(b.created_at)}
                  </TableCell>
                  <TableCell className="text-sm">
                    <Badge
                      variant="outline"
                      className={
                        b.status === "gutgeschrieben"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : b.status === "zurueckgebucht"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }
                    >
                      {WELTEN_PAKET_STATUS_LABEL[b.status] || b.status}
                    </Badge>
                    {b.teilerstattung_hinweis && (
                      <span
                        className="ml-2 text-xs text-red-600"
                        data-testid={`ops-welten-paket-partial-${b.stripe_session_id}`}
                      >
                        Teilerstattung – prüfen
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right whitespace-nowrap">
                    {fmtEuroNet(b.betrag_netto_cents)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">
                    {shortId(b.stripe_session_id)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </DetailSection>
  );
}

function TenantDetailDialog({ tenantId, fallbackName, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pinResetOpen, setPinResetOpen] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    setLoading(true);
    setError("");
    setDetail(null);
    opsApi
      .tenantDetail(tenantId)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((e) => {
        if (active) setError(opsErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId]);

  const info = detail?.info;
  const intervalLabel =
    info?.billing_interval === "yearly"
      ? "jährlich"
      : info?.billing_interval === "monthly"
      ? "monatlich"
      : "–";

  return (
    <Dialog open={!!tenantId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[88vh] overflow-y-auto"
        data-testid="ops-tenant-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="ops-detail-title">
            {info?.company_name || fallbackName || "Mandant"}
          </DialogTitle>
          <DialogDescription>
            Detailansicht{info?.company_code ? ` · ${info.company_code}` : ""}
            {tenantId ? ` · ${shortId(tenantId)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 flex justify-center" data-testid="ops-detail-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}
        {error && !loading && (
          <p className="text-sm text-red-600 py-6" data-testid="ops-detail-error">
            {error}
          </p>
        )}

        {detail && !loading && (
          <div className="space-y-4">
            {/* Bereich 1 – Mandanteninformationen */}
            <DetailSection title="Mandanteninformationen" icon={Info} testId="ops-detail-info">
              <div className="grid sm:grid-cols-2 gap-x-10">
                <InfoRow label="Firmenname" value={info.company_name} testId="ops-detail-company-name" />
                <InfoRow label="Firmencode" value={info.company_code} />
                <InfoRow label="tenant_id" value={shortId(info.tenant_id)} />
                <InfoRow label="Status" value={STATUS_LABEL[info.account_status] || info.account_status} />
                {/* FIX-01b-2 (E1, Betreiberfreigabe 12.10.): zentraler Zugriffszustand, nur lesend. */}
                {info.access_state && (
                  <InfoRow
                    label="Zugriff"
                    testId="ops-detail-zugriff"
                    value={
                      info.access_state.read_only
                        ? `Lesezugriff – ${ACCESS_REASON_LABEL[info.access_state.reason] || info.access_state.reason || "—"}`
                        : "Vollzugriff"
                    }
                  />
                )}
                {info.access_state?.aktiv_ohne_zahlung && (
                  <InfoRow label="Kennzeichen" value="aktiv ohne Zahlung" testId="ops-detail-aktiv-ohne-zahlung" />
                )}
                <InfoRow label="Trial" value={info.trial_status} />
                <InfoRow label="Tarif" value={info.plan_name} />
                {/* LIMIT-01 (Betreiberfreigabe 12.10.): read-only Hinweis bei aktivem Abo > 50 MA. */}
                {info.ueber_limit_hinweis && (
                  <InfoRow
                    label="Mitarbeiter"
                    value="über 50 Mitarbeiter – Angebot prüfen"
                    testId="ops-detail-limit-hinweis"
                  />
                )}
                <InfoRow
                  label="Welten-Kontingent"
                  value={
                    info.world_quota_exempt
                      ? `ausgenommen (${info.world_quota_klassifikation || "—"})`
                      : `${info.world_quota_used ?? 0} / ${
                          info.world_quota_limit ?? "—"
                        } genutzt${
                          info.world_quota_reserved
                            ? ` · ${info.world_quota_reserved} reserviert`
                            : ""
                        } (${info.world_quota_klassifikation || "—"})`
                  }
                  testId="ops-detail-world-quota"
                />
                <InfoRow label="Intervall" value={intervalLabel} />
                <InfoRow label="Ablaufdatum" value={fmtDate(info.trial_ends_at)} />
                <InfoRow
                  label="Tage Restlaufzeit"
                  value={
                    info.days_until_expiry == null
                      ? info.account_status === "expired"
                        ? "Abgelaufen"
                        : "–"
                      : info.days_until_expiry
                  }
                  testId="ops-detail-days-left"
                />
              </div>
            </DetailSection>

            {/* Bereich 2 – Mitarbeiter */}
            <DetailSection title="Mitarbeiter" icon={Users} count={detail.employees.length} testId="ops-detail-employees">
              {detail.employees.length === 0 ? (
                <NoData testId="ops-detail-employees-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Sprache</TableHead>
                        <TableHead>Archiviert</TableHead>
                        <TableHead>Letzter Login</TableHead>
                        <TableHead>PIN geändert</TableHead>
                        <TableHead className="text-right">Welten</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.employees.map((e) => (
                        <TableRow key={e.id} data-testid={`ops-detail-employee-${e.id}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{e.name || "–"}</TableCell>
                          <TableCell className="text-sm">{langLabel(e.preferred_language)}</TableCell>
                          <TableCell className="text-sm">{e.archived ? "ja" : "nein"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{fmtDateTime(e.last_login_at)}</TableCell>
                          <TableCell className="text-sm text-slate-600">{fmtDate(e.pin_changed_at)}</TableCell>
                          <TableCell className="text-right text-sm">{e.assigned_world_count ?? 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DetailSection>

            {/* Bereich 3 – Welten */}
            <DetailSection title="Welten" icon={Globe} count={detail.worlds.length} testId="ops-detail-worlds">
              {detail.worlds.length === 0 ? (
                <NoData testId="ops-detail-worlds-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Titel</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Quelle</TableHead>
                        <TableHead>Freigegeben</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Erstellt</TableHead>
                        <TableHead>Letzte Änderung</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.worlds.map((w) => (
                        <TableRow key={w.id} data-testid={`ops-detail-world-${w.id}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{w.title || "–"}</TableCell>
                          <TableCell className="text-sm">{w.version || "–"}</TableCell>
                          <TableCell className="text-sm">{w.source === "ai" ? "KI" : "manuell"}</TableCell>
                          <TableCell className="text-sm">{w.released ? "ja" : "nein"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{w.generation_status || "–"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{fmtDate(w.created_at)}</TableCell>
                          <TableCell className="text-sm text-slate-600">{fmtDate(w.updated_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DetailSection>

            {/* Bereich 4 – Übersetzungen */}
            <DetailSection title="Übersetzungen" icon={Languages} testId="ops-detail-translations">
              {detail.translations.length === 0 ? (
                <NoData testId="ops-detail-translations-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sprache</TableHead>
                        {TRANSLATION_BUCKETS.map(([k]) => (
                          <TableHead key={k} className="text-right capitalize">{k}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.translations.map((row) => (
                        <TableRow key={row.language} data-testid={`ops-detail-translation-${row.language}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{langLabel(row.language)}</TableCell>
                          {TRANSLATION_BUCKETS.map(([k, cls]) => (
                            <TableCell key={k} className={`text-right text-sm ${row[k] > 0 ? cls + " font-semibold" : "text-slate-300"}`}>
                              {row[k] ?? 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DetailSection>

            {/* Bereich 4b – Sprachen (Sprach-Entitlement, Schreibaktion mit
                Firmencode-Bestätigung + Kosten-Hinweis beim Hinzufügen) */}
            <LanguageEntitlementSection
              detail={detail}
              tenantId={tenantId}
              onEntitlementSaved={(res) =>
                setDetail((prev) =>
                  prev
                    ? {
                        ...prev,
                        language_entitlement: {
                          entitled_languages: res.entitled_languages || [],
                          multilanguage_included: res.multilanguage_included === true,
                        },
                      }
                    : prev,
                )
              }
            />

            {/* Bereich 5 – KI-Nutzung (Vorbereitung auf Ledger, ohne Kosten) */}
            <DetailSection title="KI-Nutzung" icon={Cpu} testId="ops-detail-ai-usage">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <KpiCard label="KI-generierte Welten" value={detail.ai_usage.ai_generated_world_count ?? 0} testId="ops-detail-ai-worlds" />
                <KpiCard label="Übersetzungen" value={detail.ai_usage.translation_count ?? 0} testId="ops-detail-ai-translations" />
                <KpiCard label="Usage-Events" value={detail.ai_usage.usage_events_count ?? 0} testId="ops-detail-ai-events" />
              </div>
              <div className="grid sm:grid-cols-2 gap-x-10 mt-3">
                <InfoRow label="Erste Nutzung" value={fmtDateTime(detail.ai_usage.first_usage_at)} testId="ops-detail-ai-first" />
                <InfoRow label="Letzte Nutzung" value={fmtDateTime(detail.ai_usage.last_usage_at)} testId="ops-detail-ai-last" />
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Reine Nutzungsfakten – keine Kosten, keine Hochrechnung.
              </p>
            </DetailSection>

            {/* Bereich 5b – Abrechnung · Nachbearbeitung (ZAHL-01 W1):
                offene/erledigte fehlgeschlagene Subscription-Kündigungen bzw.
                Dubletten des Mandanten + Aktion „Kündigung erneut versuchen". */}
            <BillingFollowupsSection tenantId={tenantId} />

            {/* Bereich 5c – Welten-Pakete (WELT-02b, Betreiberfreigabe 12.10.):
                read-only Zusatzkontingent + Paketbuchungen, keine Bearbeitung. */}
            <WeltenPaketSection tenantId={tenantId} />

            {/* ZAHL-02c (Betreiberfreigabe 13.10., K5): Vertrag je Mandant – Laufzeit, Kündigung eintragen/zurücknehmen. */}
            <OpsVertragSection tenantId={tenantId} />

            {/* MOD-01b-4 (Betreiberfreigabe 13.10., D4): Abrechnung je Mandant – nur lesend. */}
            <OpsAbrechnungSection tenantId={tenantId} />

            {/* Bereich 6 – Aktionen (2026-09 PIN-Reset; Juni 2026: zweite
                Schreibaktion ist die Sprachen-Sektion (Bereich 4b); gleiche
                Zugriffsstufe wie alle Ops-Aktionen — OPS_API_TOKEN via opsApi). */}
            <DetailSection title="Aktionen" icon={KeyRound} testId="ops-detail-actions">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-slate-600 max-w-md">
                  Setzt den Owner-Master-PIN neu (Kundenfall „PIN vergessen").
                  Alle Owner-Sitzungen werden abgemeldet; beim nächsten Login
                  wählt der Kunde einen eigenen PIN.
                </p>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="ops-detail-pin-reset-open"
                  onClick={() => setPinResetOpen(true)}
                >
                  Owner-PIN zurücksetzen
                </Button>
              </div>
            </DetailSection>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ops-detail-close">
            Schließen
          </Button>
        </DialogFooter>

        <OwnerPinResetDialog
          open={pinResetOpen}
          onClose={() => setPinResetOpen(false)}
          tenantId={tenantId}
          companyName={info?.company_name || fallbackName}
        />
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// P2.2 – OPS Console V2: Übersetzungszentrum (READ-ONLY, nur Anzeige).
// Globale KPIs + je-Mandant-Tabelle (pro Sprache) + Fehlerliste. Keine Steuerung,
// kein Retry, keine Kosten. Fehlende Daten erscheinen als „keine Daten“.
// ---------------------------------------------------------------------------

const TC_BUCKETS = [
  ["complete", "text-emerald-700"],
  ["partial", "text-sky-700"],
  ["pending", "text-slate-500"],
  ["processing", "text-indigo-700"],
  ["error", "text-red-600"],
  ["stale", "text-amber-700"],
];

function TranslationCenterSection() {
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      opsApi.translationOverview(),
      opsApi.translationTenants(),
      opsApi.translationErrors(50),
    ])
      .then(([ov, tn, er]) => {
        setOverview(ov);
        setTenants(tn.tenants || []);
        setErrors(er.errors || []);
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const jobs = overview?.jobs?.by_status || {};
  const tr = overview?.translations?.by_status || {};

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-translations-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Languages className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Übersetzungen</h2>
            <p className="text-xs text-slate-500">
              Read-only Sicht auf die Übersetzungs-Pipeline – keine Steuerung, kein Retry, keine Kosten.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ops-translations-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center" data-testid="ops-translations-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : loadErr ? (
        <p className="text-sm text-red-600 p-5" data-testid="ops-translations-error">{loadErr}</p>
      ) : (
        <div className="p-5 space-y-6">
          {/* Globale KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ops-translations-kpis">
            <KpiCard label="Mandanten m. Übers." value={overview?.tenants_with_translations ?? 0} testId="ops-tc-kpi-tenants" />
            <KpiCard label="Sprachen" value={(overview?.languages || []).length} testId="ops-tc-kpi-languages" accent="text-indigo-600" />
            <KpiCard label="Jobs pending" value={jobs.pending ?? 0} testId="ops-tc-kpi-pending" accent="text-slate-600" />
            <KpiCard label="Jobs processing" value={jobs.processing ?? 0} testId="ops-tc-kpi-processing" accent="text-indigo-600" />
            <KpiCard label="Jobs done" value={jobs.done ?? 0} testId="ops-tc-kpi-done" accent="text-emerald-600" />
            <KpiCard label="Jobs error" value={jobs.error ?? 0} testId="ops-tc-kpi-error" accent={(jobs.error ?? 0) > 0 ? "text-red-600" : "text-slate-900"} />
            <KpiCard label="Übers. complete" value={tr.complete ?? 0} testId="ops-tc-kpi-complete" accent="text-emerald-600" />
            <KpiCard label="Übers. partial" value={tr.partial ?? 0} testId="ops-tc-kpi-partial" accent="text-sky-600" />
            <KpiCard label="Übers. stale" value={tr.stale ?? 0} testId="ops-tc-kpi-stale" accent="text-amber-600" />
          </div>

          {/* Tabelle je Mandant × Sprache */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Je Mandant &amp; Sprache</h3>
            {tenants.length === 0 ? (
              <NoData testId="ops-translations-tenants-empty" />
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <Table data-testid="ops-translations-tenants-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Firma</TableHead>
                      <TableHead>Sprache</TableHead>
                      <TableHead className="text-right">MA</TableHead>
                      <TableHead className="text-right">Welten</TableHead>
                      <TableHead className="text-right">Released</TableHead>
                      {TC_BUCKETS.map(([k]) => (
                        <TableHead key={k} className="text-right capitalize">{k}</TableHead>
                      ))}
                      <TableHead>Letzte Übers.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenants.flatMap((t) =>
                      (t.by_language.length ? t.by_language : [{ language: "?" }]).map((row, idx) => (
                        <TableRow key={`${t.tenant_id}-${row.language}`} data-testid={`ops-tc-row-${t.tenant_id}-${row.language}`}>
                          <TableCell className="text-sm">
                            {idx === 0 ? (
                              <>
                                <div className="font-medium text-slate-800">{t.company_name || "–"}</div>
                                <div className="text-xs text-slate-400">{t.company_code || shortId(t.tenant_id)}</div>
                              </>
                            ) : (
                              <span className="text-slate-300">↳</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-medium">{langLabel(row.language)}</TableCell>
                          <TableCell className="text-right text-sm">{t.employees_by_language?.[row.language] ?? 0}</TableCell>
                          <TableCell className="text-right text-sm">{idx === 0 ? t.worlds_total : ""}</TableCell>
                          <TableCell className="text-right text-sm">{idx === 0 ? t.worlds_released : ""}</TableCell>
                          {TC_BUCKETS.map(([k, cls]) => (
                            <TableCell key={k} className={`text-right text-sm ${row[k] > 0 ? cls + " font-semibold" : "text-slate-300"}`}>
                              {row[k] ?? 0}
                            </TableCell>
                          ))}
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                            {idx === 0 ? fmtDate(t.last_translation_at) : ""}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Fehlerliste */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">
              Fehlgeschlagene Jobs
            </h3>
            {errors.length === 0 ? (
              <NoData testId="ops-translations-errors-empty" />
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <Table data-testid="ops-translations-errors-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Firma</TableHead>
                      <TableHead>Sprache</TableHead>
                      <TableHead>Welt</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Versuche</TableHead>
                      <TableHead>Fehlercode</TableHead>
                      <TableHead>Fehlerklasse</TableHead>
                      <TableHead>Aktualisiert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((e, i) => (
                      <TableRow key={`${e.tenant_id}-${e.course_id}-${e.language}-${i}`} data-testid={`ops-tc-error-${i}`}>
                        <TableCell className="text-sm">{e.company_name || shortId(e.tenant_id)}</TableCell>
                        <TableCell className="text-sm">{langLabel(e.language)}</TableCell>
                        <TableCell className="text-sm">{e.course_title || e.course_id}</TableCell>
                        <TableCell className="text-sm text-red-600">{e.status}</TableCell>
                        <TableCell className="text-right text-sm">{e.attempts ?? 0}/{e.max_attempts ?? "–"}</TableCell>
                        <TableCell className="text-sm">{e.last_error_code || "–"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.last_error_class || "–"}</TableCell>
                        <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDateTime(e.updated_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// P2.3a – OPS Console V2: Verbrauchszentrum OHNE EURO (READ-ONLY).
// Tokens/Aufrufe je Mandant/Modell/Art aus usage_events. KEINE Euro, keine Preise,
// keine Rollups. Zeitfilter all/today/7d/30d. Fehlende Daten → „keine Daten“.
// ---------------------------------------------------------------------------

const USAGE_RANGES = [
  ["all", "Alle"],
  ["today", "Heute"],
  ["7d", "7 Tage"],
  ["30d", "30 Tage"],
];
const KIND_LABEL = {
  translation: "Übersetzung",
  ai_world_generation: "KI-Weltgenerierung",
};
function kindLabel(k) {
  return KIND_LABEL[k] || k || "–";
}

function UsageCenterSection() {
  const [range, setRange] = useState("all");
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [models, setModels] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback((r) => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      opsApi.usageOverview(r),
      opsApi.usageTenants(r),
      opsApi.usageModels(r),
      opsApi.usageKinds(r),
    ])
      .then(([ov, tn, md, kd]) => {
        setOverview(ov);
        setTenants(tn.tenants || []);
        setModels(md.models || []);
        setKinds(kd.kinds || []);
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(range);
  }, [load, range]);

  const hasData = (overview?.calls ?? 0) > 0;
  const src = overview?.by_tokens_source || {};

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-usage-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Verbrauch</h2>
            <p className="text-xs text-slate-500">
              Read-only KI-/LLM-Token-Nutzung aus dem Usage-Ledger – keine Euro, keine Preise, keine Kosten.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="ops-usage-range">
            {USAGE_RANGES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`ops-usage-range-${key}`}
                onClick={() => setRange(key)}
                className={`px-2.5 py-1 text-xs ${range === key ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load(range)} disabled={loading} data-testid="ops-usage-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center" data-testid="ops-usage-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : loadErr ? (
        <p className="text-sm text-red-600 p-5" data-testid="ops-usage-error">{loadErr}</p>
      ) : (
        <div className="p-5 space-y-6">
          {/* Globale KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ops-usage-kpis">
            <KpiCard label="LLM-Aufrufe" value={fmtNum(overview?.calls ?? 0)} testId="ops-usage-kpi-calls" />
            <KpiCard label="Input-Tokens" value={fmtNum(overview?.input_tokens ?? 0)} testId="ops-usage-kpi-input" accent="text-sky-600" />
            <KpiCard label="Output-Tokens" value={fmtNum(overview?.output_tokens ?? 0)} testId="ops-usage-kpi-output" accent="text-indigo-600" />
            <KpiCard label="Total-Tokens" value={fmtNum(overview?.total_tokens ?? 0)} testId="ops-usage-kpi-total" accent="text-slate-900" />
            <KpiCard label="Mandanten m. Usage" value={overview?.tenants_with_usage ?? 0} testId="ops-usage-kpi-tenants" />
            <KpiCard label="Modelle" value={(overview?.models || []).length} testId="ops-usage-kpi-models" />
            <KpiCard label="actual" value={fmtNum(src.actual ?? 0)} testId="ops-usage-kpi-actual" accent="text-emerald-600" />
            <KpiCard label="estimated" value={fmtNum(src.estimated ?? 0)} testId="ops-usage-kpi-estimated" accent="text-amber-600" />
          </div>

          {!hasData ? (
            <NoData testId="ops-usage-empty" />
          ) : (
            <>
              {/* Je Mandant */}
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Je Mandant</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-usage-tenants-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Firma</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Input</TableHead>
                        <TableHead className="text-right">Output</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Übers.</TableHead>
                        <TableHead className="text-right">KI-Welt</TableHead>
                        <TableHead className="text-right">actual</TableHead>
                        <TableHead className="text-right">estimated</TableHead>
                        <TableHead>Erste</TableHead>
                        <TableHead>Letzte</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((t) => (
                        <TableRow key={t.tenant_id} data-testid={`ops-usage-tenant-${t.tenant_id}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{t.company_name || (t.tenant_id ? shortId(t.tenant_id) : "(ohne Mandant)")}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.input_tokens)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.output_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtNum(t.total_tokens)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.by_kind?.translation?.calls ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.by_kind?.ai_world_generation?.calls ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm text-emerald-700">{fmtNum(t.by_tokens_source?.actual ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm text-amber-700">{fmtNum(t.by_tokens_source?.estimated ?? 0)}</TableCell>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDate(t.first_usage_at)}</TableCell>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDate(t.last_usage_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Modellübersicht */}
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Modelle</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-usage-models-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Modell</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Input</TableHead>
                        <TableHead className="text-right">Output</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">actual</TableHead>
                        <TableHead className="text-right">estimated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {models.map((m) => (
                        <TableRow key={m.model} data-testid={`ops-usage-model-${m.model}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{m.model}</TableCell>
                          <TableCell className="text-sm">{m.provider || "–"}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(m.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(m.input_tokens)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(m.output_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtNum(m.total_tokens)}</TableCell>
                          <TableCell className="text-right text-sm text-emerald-700">{fmtNum(m.by_tokens_source?.actual ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm text-amber-700">{fmtNum(m.by_tokens_source?.estimated ?? 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Artübersicht */}
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Nach Art</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-usage-kinds-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Art</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Input</TableHead>
                        <TableHead className="text-right">Output</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kinds.map((k) => (
                        <TableRow key={k.kind} data-testid={`ops-usage-kind-${k.kind}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{kindLabel(k.kind)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(k.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(k.input_tokens)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(k.output_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtNum(k.total_tokens)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                Reine Nutzungsfakten (Tokens). Keine Euro, keine Preise, keine Hochrechnung.
                „actual“ = Provider-Ist, „estimated“ = deterministische Schätzung.
              </p>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// P2.3b-1 – OPS Console V2: Kostenzentrum (READ-ONLY, abgeleitet aus price_table).
// Kosten = usage_events (Tokens) × point-in-time Preis. KEINE Marge, kein Umsatz,
// kein Editor. Fehlt ein Preis → „ohne Preis“ (nicht 0). Geld intern in µ€.
// ---------------------------------------------------------------------------

const COST_GRANULARITIES = [
  ["day", "Tag"],
  ["month", "Monat"],
  ["year", "Jahr"],
];

function CostCenterSection() {
  const [range, setRange] = useState("all");
  const [granularity, setGranularity] = useState("day");
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [models, setModels] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback((r, g) => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      opsApi.costOverview(r),
      opsApi.costTenants(r),
      opsApi.costModels(r),
      opsApi.costTimeseries(r, g),
    ])
      .then(([ov, tn, md, ts]) => {
        setOverview(ov);
        setTenants(tn.tenants || []);
        setModels(md.models || []);
        setSeries(ts.series || []);
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(range, granularity);
  }, [load, range, granularity]);

  const unpriced = overview?.unpriced_calls ?? 0;
  const hasData = (overview?.calls ?? 0) > 0;

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-costs-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Kosten</h2>
            <p className="text-xs text-slate-500">
              Read-only, abgeleitet aus Verbrauch × versionierter Preis-Tabelle. NETTO, ohne USt. Keine Marge, kein Umsatz.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="ops-costs-range">
            {USAGE_RANGES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`ops-costs-range-${key}`}
                onClick={() => setRange(key)}
                className={`px-2.5 py-1 text-xs ${range === key ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load(range, granularity)} disabled={loading} data-testid="ops-costs-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center" data-testid="ops-costs-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : loadErr ? (
        <p className="text-sm text-red-600 p-5" data-testid="ops-costs-error">{loadErr}</p>
      ) : (
        <div className="p-5 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ops-costs-kpis">
            <KpiCard label="Gesamtkosten" value={fmtEuroMicro(overview?.cost_micro_eur ?? 0)} testId="ops-costs-kpi-total" accent="text-slate-900" />
            <KpiCard label="LLM-Aufrufe" value={fmtNum(overview?.calls ?? 0)} testId="ops-costs-kpi-calls" />
            <KpiCard label="mit Preis" value={fmtNum(overview?.priced_calls ?? 0)} testId="ops-costs-kpi-priced" accent="text-emerald-600" />
            <KpiCard label="ohne Preis" value={fmtNum(unpriced)} testId="ops-costs-kpi-unpriced" accent={unpriced > 0 ? "text-red-600" : "text-slate-900"} />
            <KpiCard label="Modelle ohne Preis" value={(overview?.models_unpriced || []).length} testId="ops-costs-kpi-models-unpriced" accent={(overview?.models_unpriced || []).length > 0 ? "text-amber-600" : "text-slate-900"} />
          </div>

          {unpriced > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2" data-testid="ops-costs-unpriced-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-xs text-amber-800">
                {fmtNum(unpriced)} Aufruf(e) ohne hinterlegten Preis – diese sind NICHT in den Kosten enthalten.
                Betroffene Modelle: {(overview?.models_unpriced || []).join(", ") || "–"}.
              </p>
            </div>
          )}

          {!hasData ? (
            <NoData testId="ops-costs-empty" />
          ) : (
            <>
              {/* Je Mandant */}
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Je Mandant</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-costs-tenants-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Firma</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Total-Tokens</TableHead>
                        <TableHead className="text-right">Kosten</TableHead>
                        <TableHead className="text-right">ohne Preis</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((t) => (
                        <TableRow key={t.tenant_id} data-testid={`ops-costs-tenant-${t.tenant_id}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{t.company_name || (t.tenant_id ? shortId(t.tenant_id) : "(ohne Mandant)")}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(t.total_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtEuroMicro(t.cost_micro_eur)}</TableCell>
                          <TableCell className={`text-right text-sm ${t.unpriced_calls > 0 ? "text-red-600" : "text-slate-300"}`}>{fmtNum(t.unpriced_calls)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Je Modell */}
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800 mb-2">Je Modell</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-costs-models-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Modell</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Total-Tokens</TableHead>
                        <TableHead className="text-right">Kosten</TableHead>
                        <TableHead>Preis hinterlegt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {models.map((m) => (
                        <TableRow key={m.model} data-testid={`ops-costs-model-${m.model}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{m.model}</TableCell>
                          <TableCell className="text-sm">{m.provider || "–"}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(m.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(m.total_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{m.has_price ? fmtEuroMicro(m.cost_micro_eur) : <span className="text-red-600">kein Preis</span>}</TableCell>
                          <TableCell className="text-sm">{m.has_price ? <span className="text-emerald-700">ja</span> : <span className="text-red-600">nein</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Zeitreihe */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-sm font-semibold text-slate-800">Zeitverlauf</h3>
                  <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="ops-costs-granularity">
                    {COST_GRANULARITIES.map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        data-testid={`ops-costs-gran-${key}`}
                        onClick={() => setGranularity(key)}
                        className={`px-2.5 py-1 text-xs ${granularity === key ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table data-testid="ops-costs-timeseries-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Zeitraum (UTC)</TableHead>
                        <TableHead className="text-right">Aufrufe</TableHead>
                        <TableHead className="text-right">Total-Tokens</TableHead>
                        <TableHead className="text-right">Kosten</TableHead>
                        <TableHead className="text-right">ohne Preis</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {series.map((s) => (
                        <TableRow key={s.bucket} data-testid={`ops-costs-bucket-${s.bucket}`}>
                          <TableCell className="text-sm font-medium text-slate-800">{s.bucket}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(s.calls)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtNum(s.total_tokens)}</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtEuroMicro(s.cost_micro_eur)}</TableCell>
                          <TableCell className={`text-right text-sm ${s.unpriced_calls > 0 ? "text-red-600" : "text-slate-300"}`}>{fmtNum(s.unpriced_calls)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                NETTO, ohne USt. Abgeleitet aus der versionierten Preis-Tabelle (Bewertung nach Nutzungszeitpunkt).
                Preise aktuell Platzhalter (0,00 €) – echte Werte trägt der Betreiber später ein. Keine Marge, kein Umsatz.
              </p>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Hauptkomponente
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// P2.4a (Juni 2026) – OPS Console V2: Dashboard (READ-ONLY Navigations-Hub).
//
// Reine Kompositions-/Überblicksschicht über den neuen Endpoint
// `GET /api/ops/dashboard/summary`. KEINE neue Geschäftslogik, KEINE Writes,
// KEINE Charts/Trends/Top-Listen. KPI-Kacheln sind klickbare Sprung-Anker zu den
// bereits bestehenden Abschnitten (sanftes Scrollen + kurze Hervorhebung).
// ---------------------------------------------------------------------------

function scrollToSection(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  try {
    el.style.transition = "box-shadow 0.3s ease";
    el.style.borderRadius = "0.5rem";
    el.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.55)";
    setTimeout(() => {
      el.style.boxShadow = "";
    }, 1500);
  } catch {
    /* noop */
  }
}

function DashKpi({ label, value, sub, accent, testId, onClick, active }) {
  const clickable = typeof onClick === "function";
  return (
    <Card
      data-testid={testId}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={clickable ? "Zum zugehörigen Abschnitt springen" : undefined}
      className={`bg-white border px-4 py-3 flex flex-col gap-0.5 transition-shadow ${
        active
          ? "border-slate-900 border-2 shadow-md bg-slate-50"
          : "border-slate-200"
      } ${
        clickable
          ? "cursor-pointer hover:shadow-md hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          : ""
      }`}
    >
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`font-heading text-2xl font-bold ${accent || "text-slate-900"}`}>{value}</span>
      {sub != null && <span className="text-xs text-slate-400">{sub}</span>}
    </Card>
  );
}

function AmpelDot({ ok, label, note, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white"
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
      <span className="text-sm text-slate-700 flex-1">
        {label}
        {note ? <span className="text-xs text-slate-400"> · {note}</span> : null}
      </span>
      <span className={`text-xs font-medium ${ok ? "text-emerald-600" : "text-red-600"}`}>
        {ok ? "OK" : "Fehler"}
      </span>
    </div>
  );
}

function DashSubhead({ children }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-1">
      {children}
    </h3>
  );
}

// OPS-DASHBOARD-SPLIT (2026-08-21, Betreiberauftrag Option A):
// Die ehemals monolithische `DashboardSection` (eine Card mit Header +
// vier Blöcken) wird aufgeteilt in:
//   1) `useDashboardSummary()` — Custom-Hook: State + Laden + abgeleitete
//      Werte. Wird EIN Mal im Elternteil (`OpsConsole`) aufgerufen; kein
//      Hook-Aufruf in den Sub-Komponenten selbst → garantiert genau
//      einen `opsApi.dashboardSummary()`-Call pro Refresh.
//   2) `DashboardSectionTop` — Card mit Kopfzeile („Dashboard" +
//      „Aktualisieren"-Button), Loading-/Fehler-Zustand, Block 1
//      „Gesamtstatus" (die 8 klickbaren Filter-Kacheln, Verhalten und
//      Testids unverändert).
//   3) `DashboardSectionBottom` — Card OHNE eigenen Header, Blöcke 2/3/4
//      („Verbrauch & Kosten", „Technischer Status", „Ampeln"). Rendert
//      nur, wenn `dash.summary` vorliegt.
// Auf der Hauptseite platziert `OpsConsole` diese in der Reihenfolge
// Top → Mandanten-Abschnitt → Bottom. Der „Aktualisieren"-Button oben
// aktualisiert weiterhin Werte in beiden Cards, weil beide dieselbe
// `dash`-Prop-Instanz konsumieren.
function useDashboardSummary({ enabled = true } = {}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    opsApi
      .dashboardSummary()
      .then(setSummary)
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // OPS-DASHBOARD-SPLIT (2026-08-21): Auto-Load nur, wenn vom Aufrufer
    // freigegeben (z. B. erst nach bestandener Auth-Gate im Elternteil).
    // Erhält die OPS-504-FIX-Isolation: kein Aufruf des Endpunkts
    // `/api/ops/dashboard/summary`, solange `authed === false`.
    if (enabled) load();
  }, [load, enabled]);

  const t = summary?.tenants || {};
  const p = summary?.platform || {};
  const pdf = summary?.pdf_outbox?.by_status || {};
  const tj = summary?.translation_jobs?.by_status || {};
  const usage = summary?.usage || {};
  const costs = summary?.costs || {};
  const comp = summary?.health?.components || {};

  const pdfError = (pdf.error || 0) + (pdf.orphaned || 0);
  const trError = tj.error || 0;
  const trOpen = (tj.pending || 0) + (tj.processing || 0);

  return {
    summary, loading, loadErr, load,
    t, p, pdf, tj, usage, costs, comp,
    pdfError, trError, trOpen,
  };
}

function DashboardSectionTop({ dash, statusFilter, onFilterChange }) {
  const { summary, loading, loadErr, load, t } = dash;

  const toTenants = () => scrollToSection("ops-section-tenants");

  // OPS-STATUS-FILTER (Feb 2026, betreibergenehmigt, Auftrag „Status-Kacheln
  // als Filter"): Klick auf Status-Kachel scrollt zur Tabelle UND toggelt den
  // Filter. Klick auf gerade aktiven Filter setzt zurück auf den Standard-
  // Zustand ("hide_suspended", Gesperrt ausgeblendet). Klick auf die
  // Gesamt-Kachel „Mandanten" setzt auf „all" (alle inkl. Gesperrt) bzw.
  // zurück auf Standard.
  const toggleStatusFilter = (status) => {
    toTenants();
    if (typeof onFilterChange === "function") {
      onFilterChange((prev) => (prev === status ? "hide_suspended" : status));
    }
  };
  const toggleTotalFilter = () => {
    toTenants();
    if (typeof onFilterChange === "function") {
      onFilterChange((prev) => (prev === "all" ? "hide_suspended" : "all"));
    }
  };

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-dashboard-section-top">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Dashboard</h2>
            <p className="text-xs text-slate-500">
              Read-only Überblick &amp; Navigations-Hub – Kacheln springen zum jeweiligen Abschnitt.
            </p>
          </div>
        </div>
        <Button
          data-testid="ops-dashboard-refresh"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {loading && (
        <div className="px-5 py-10 flex items-center justify-center text-slate-400" data-testid="ops-dashboard-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && loadErr && (
        <div className="px-5 py-6" data-testid="ops-dashboard-error">
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {loadErr}
            </p>
            <Button variant="outline" size="sm" onClick={load}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {!loading && !loadErr && summary && (
        <div className="px-5 py-5">
          {/* Bereich 1 – Gesamtstatus */}
          <div>
            <DashSubhead>Gesamtstatus</DashSubhead>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
              <DashKpi label="Mandanten" value={fmtNum(t.total_tenants ?? 0)} testId="ops-dash-total" onClick={toggleTotalFilter} active={statusFilter === "all"} />
              <DashKpi label="Aktiv" value={fmtNum(t.active_tenants ?? 0)} accent="text-emerald-600" testId="ops-dash-active" onClick={() => toggleStatusFilter("active")} active={statusFilter === "active"} />
              <DashKpi label="Demo" value={fmtNum(t.demo_tenants ?? 0)} accent="text-sky-600" testId="ops-dash-demo" onClick={() => toggleStatusFilter("demo")} active={statusFilter === "demo"} />
              <DashKpi label="Abgelaufen" value={fmtNum(t.expired_tenants ?? 0)} accent="text-amber-600" testId="ops-dash-expired" onClick={() => toggleStatusFilter("expired")} active={statusFilter === "expired"} />
              <DashKpi label="Gesperrt" value={fmtNum(t.suspended_tenants ?? 0)} accent="text-red-600" testId="ops-dash-suspended" onClick={() => toggleStatusFilter("suspended")} active={statusFilter === "suspended"} />
              <DashKpi label="Läuft <7 Tage ab" value={fmtNum(t.expiring_next_7_days ?? 0)} accent="text-orange-600" testId="ops-dash-expiring" onClick={toTenants} />
              <DashKpi label="MRR netto" value={fmtEuro(t.estimated_mrr_net_cents || 0)} accent="text-indigo-600" testId="ops-dash-mrr" sub={`Potenzial ${fmtEuro(t.potential_mrr_net_cents || 0)}`} onClick={toTenants} />
              <DashKpi label="ARR netto" value={fmtEuro(t.estimated_arr_net_cents || 0)} accent="text-indigo-600" testId="ops-dash-arr" onClick={toTenants} />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function DashboardSectionBottom({ dash }) {
  const { summary, loading, loadErr, p, usage, costs, comp, pdf, pdfError, trError, trOpen } = dash;

  // Solange oben (Top-Card) noch lädt oder ein Fehler steht, rendert der
  // untere Bereich absichtlich NICHTS — die Kacheln würden sonst mit
  // Null-Werten aufblinken. Der Fehler wird ausschließlich in der Top-
  // Card angezeigt (kein doppeltes Error-Panel).
  if (loading || loadErr || !summary) return null;

  const toUsage = () => scrollToSection("ops-section-usage");
  const toCosts = () => scrollToSection("ops-section-costs");
  const toTenants = () => scrollToSection("ops-section-tenants");
  const toTranslations = () => scrollToSection("ops-section-translations");

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-dashboard-section-bottom">
      <div className="px-5 py-5 space-y-6">
        {/* Bereich 2 – Verbrauch & Kosten */}
        <div>
          <DashSubhead>Verbrauch &amp; Kosten</DashSubhead>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <DashKpi label="Tokens heute" value={fmtNum(usage.today?.total_tokens ?? 0)} sub={`${fmtNum(usage.today?.calls ?? 0)} Aufrufe`} testId="ops-dash-tokens-today" onClick={toUsage} />
            <DashKpi label="Tokens 7 Tage" value={fmtNum(usage["7d"]?.total_tokens ?? 0)} sub={`${fmtNum(usage["7d"]?.calls ?? 0)} Aufrufe`} testId="ops-dash-tokens-7d" onClick={toUsage} />
            <DashKpi label="Tokens 30 Tage" value={fmtNum(usage["30d"]?.total_tokens ?? 0)} sub={`${fmtNum(usage["30d"]?.calls ?? 0)} Aufrufe`} testId="ops-dash-tokens-30d" onClick={toUsage} />
            <DashKpi label="Kosten heute" value={fmtEuroMicro(costs.today?.cost_micro_eur)} accent="text-indigo-600" sub={(costs.today?.unpriced_calls || 0) > 0 ? `${fmtNum(costs.today.unpriced_calls)} ohne Preis` : null} testId="ops-dash-cost-today" onClick={toCosts} />
            <DashKpi label="Kosten 7 Tage" value={fmtEuroMicro(costs["7d"]?.cost_micro_eur)} accent="text-indigo-600" sub={(costs["7d"]?.unpriced_calls || 0) > 0 ? `${fmtNum(costs["7d"].unpriced_calls)} ohne Preis` : null} testId="ops-dash-cost-7d" onClick={toCosts} />
            <DashKpi label="Kosten 30 Tage" value={fmtEuroMicro(costs["30d"]?.cost_micro_eur)} accent="text-indigo-600" sub={(costs["30d"]?.unpriced_calls || 0) > 0 ? `${fmtNum(costs["30d"].unpriced_calls)} ohne Preis` : null} testId="ops-dash-cost-30d" onClick={toCosts} />
          </div>
        </div>

        {/* Bereich 3 – Technischer Status */}
        <div>
          <DashSubhead>Technischer Status</DashSubhead>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            <DashKpi label="Welten freigegeben" value={fmtNum(p.worlds_released ?? 0)} testId="ops-dash-worlds-released" />
            <DashKpi label="Nachweise gesamt" value={fmtNum(p.nachweise_total ?? 0)} testId="ops-dash-nachweise" />
            <DashKpi label="Mitarbeiter aktiv" value={fmtNum(p.employees_active ?? 0)} testId="ops-dash-employees" />
            <DashKpi label="PDF-Outbox offen" value={fmtNum(pdf.pending ?? 0)} accent="text-orange-600" testId="ops-dash-pdf-pending" onClick={toTenants} />
            <DashKpi label="PDF-Outbox Fehler" value={fmtNum(pdfError)} accent={pdfError > 0 ? "text-red-600" : "text-slate-900"} testId="ops-dash-pdf-error" onClick={toTenants} />
            <DashKpi label="Übersetzungen offen" value={fmtNum(trOpen)} accent="text-orange-600" testId="ops-dash-tr-open" onClick={toTranslations} />
            <DashKpi label="Übersetzungen Fehler" value={fmtNum(trError)} accent={trError > 0 ? "text-red-600" : "text-slate-900"} testId="ops-dash-tr-error" onClick={toTranslations} />
          </div>
        </div>

        {/* Bereich 4 – Ampeln */}
        <div>
          <DashSubhead>Ampeln</DashSubhead>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ops-dashboard-ampeln">
            <AmpelDot ok={!!comp.database?.ok} label="Datenbank" testId="ops-ampel-db" />
            <AmpelDot ok={!!comp.object_storage?.ok} label="Objektspeicher" note={comp.object_storage?.backend} testId="ops-ampel-storage" />
            <AmpelDot ok={!!comp.pdf_worker?.ok} label="PDF-Worker" testId="ops-ampel-worker" />
            <AmpelDot ok={pdfError === 0} label="PDF-Outbox" note={pdfError > 0 ? `${pdfError} betroffen` : "keine Fehler"} testId="ops-ampel-pdf-outbox" />
            <AmpelDot ok={trError === 0} label="Übersetzungsjobs" note={trError > 0 ? `${trError} Fehler` : "keine Fehler"} testId="ops-ampel-translations" />
          </div>
        </div>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// ADR-007 B – Nacharchivierungs-Dialog (Preview read-only → confirm → Requeue →
// Abschlussprüfung). Kein grüner Gesamterfolg bei Teilfehlern/offenen Jobs.
// ---------------------------------------------------------------------------

function RearchiveDialog({ tenant, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [startResult, setStartResult] = useState(null);
  const [completeResult, setCompleteResult] = useState(null);

  const tid = tenant?.tenant_id;

  const loadPreview = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    setError("");
    try {
      setPreview(await opsApi.rearchivePreview(tid));
    } catch (err) {
      setError(opsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    if (tid) {
      setPreview(null);
      setReason("");
      setStartResult(null);
      setCompleteResult(null);
      loadPreview();
    }
  }, [tid, loadPreview]);

  if (!tenant) return null;

  const jobs = preview?.jobs || {};
  const openWork =
    (preview?.nachweise_unarchiviert || 0) > 0 ||
    (jobs.pending || 0) > 0 ||
    (jobs.processing || 0) > 0 ||
    (jobs.error || 0) > 0;
  const fullyDone = preview && !openWork;

  async function start() {
    setBusy(true);
    setError("");
    try {
      setStartResult(await opsApi.rearchiveStart(tid, reason.trim()));
      toast.success("Nacharchivierung gestartet – der Sweeper verarbeitet die Jobs.");
      await loadPreview();
    } catch (err) {
      setError(opsErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    setBusy(true);
    setError("");
    try {
      const res = await opsApi.rearchiveComplete(tid);
      setCompleteResult(res);
      if (res.fully_archived) toast.success("Nacharchivierung vollständig abgeschlossen.");
      else toast.warning("Noch offene oder fehlgeschlagene Jobs – kein Gesamterfolg.");
      await loadPreview();
    } catch (err) {
      setError(opsErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const kBadge = (ok, label) => (
    <Badge
      variant="outline"
      className={ok ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-700"}
    >
      {label}: {ok ? "erfüllt" : "fehlt"}
    </Badge>
  );

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="ops-rearchive-dialog">
        <DialogHeader>
          <DialogTitle>Nacharchivierung (ADR-007 B)</DialogTitle>
          <DialogDescription>
            {tenant.company_name} · <span className="font-mono text-xs">{tid}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="ops-rearchive-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Bestandsaufnahme wird geladen …
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600" data-testid="ops-rearchive-error">{error}</p>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="ops-rearchive-attestation">
              {kBadge(preview.k1_trial_converted, "K1 konvertiert")}
              {kBadge(preview.k2_worm_write_authorized, "K2 Merkmal")}
              {kBadge(preview.k3_ops_audit_upgraded, "K3 Audit")}
            </div>

            {!preview.attestation_complete && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="ops-rearchive-blocked">
                Keine vollständige Konversions-Attestierung – Nacharchivierung ist
                fail-closed gesperrt. Zuerst das OPS-Upgrade ausführen.
              </p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="ops-rearchive-counts">
              <div className="border rounded p-2">Nachweise<br /><b>{preview.nachweise_total}</b></div>
              <div className="border rounded p-2">archiviert<br /><b>{preview.nachweise_archiviert}</b></div>
              <div className="border rounded p-2">unarchiviert<br /><b data-testid="ops-rearchive-unarchived">{preview.nachweise_unarchiviert}</b></div>
              <div className="border rounded p-2">requeue-fähig<br /><b>{preview.requeueable_error_jobs}</b></div>
              <div className="border rounded p-2">pending<br /><b>{jobs.pending}</b></div>
              <div className="border rounded p-2">processing<br /><b>{jobs.processing}</b></div>
              <div className="border rounded p-2">error<br /><b data-testid="ops-rearchive-error-count">{jobs.error}</b></div>
              <div className="border rounded p-2">done<br /><b>{jobs.done}</b></div>
            </div>

            {(preview.anomalie_nachweise_ohne_job || []).length > 0 && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="ops-rearchive-anomaly">
                Anomalie: {preview.anomalie_nachweise_ohne_job.length} unarchivierte
                Nachweise OHNE Outbox-Job (Phase-A-Verletzung) – separate
                Betreiberentscheidung nötig, wird hier NICHT automatisch behandelt.
              </p>
            )}

            {fullyDone && (
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="ops-rearchive-alldone">
                Alles archiviert – keine offenen, fehlerhaften oder unarchivierten Bestände.
              </p>
            )}

            {preview.attestation_complete && preview.requeueable_error_jobs > 0 && (
              <div className="space-y-2">
                <Label htmlFor="rearchive-reason">Begründung (Pflicht, wird auditiert)</Label>
                <Input
                  id="rearchive-reason"
                  data-testid="ops-rearchive-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="z. B. Nacharchivierung nach OPS-Konversion (ADR-007 B)"
                />
              </div>
            )}

            {startResult && (
              <p className="text-sm text-slate-600" data-testid="ops-rearchive-startresult">
                Requeue: {startResult.requeued_jobs} Job(s) neu eingereiht,
                {" "}{startResult.skipped_error_jobs} übersprungen.
              </p>
            )}
            {completeResult && !completeResult.fully_archived && (
              <p className="text-sm text-amber-700" data-testid="ops-rearchive-partial">
                Teilzustand: noch {completeResult.nachweise_unarchiviert} unarchiviert,
                {" "}{completeResult.jobs?.error} Fehler, {completeResult.jobs?.pending} offen.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="ops-rearchive-refresh" disabled={busy || loading} onClick={loadPreview}>
            <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
          </Button>
          <Button
            variant="outline"
            data-testid="ops-rearchive-complete"
            disabled={busy || loading || !preview}
            onClick={complete}
          >
            Abschluss prüfen
          </Button>
          <Button
            data-testid="ops-rearchive-start"
            disabled={
              busy || loading || !preview || !preview.attestation_complete ||
              preview.requeueable_error_jobs === 0 || !reason.trim()
            }
            onClick={start}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Nacharchivierung starten"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// CLARTEXT · Block H Nachtrag (Feb 2026) — OPS-Center-UI für Modul-Add-ons.
// Generisch über ALLE registrierten Module (Registry via opsApi.moduleAddons,
// Betreiberantwort 5 — KEIN modulspezifisches Hardcoding); erzwingt
// Aktivierung-vor-Preis (Antwort 3); vertragslose Mandanten: Button
// deaktiviert + Hinweis statt 409 nach Klick (Antwort 6); laufendes Abo:
// reiner Anzeige-Zustand, KEINE Preisänderung (Antwort 7).
// ---------------------------------------------------------------------------

// Euro-Eingabe mit Komma (Antwort 4) → Netto-Cent. Ungültig → null.
function parseEuroToNetCents(text) {
  const m = /^(\d{1,6})(?:[.,](\d{1,2}))?$/.exec(String(text || "").trim());
  if (m === null) return null;
  return parseInt(m[1], 10) * 100 + parseInt((m[2] || "0").padEnd(2, "0"), 10);
}

function fmtAddonEuro(cents) {
  if (cents == null) return "–";
  return (
    (cents / 100).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

const ADDON_SUB_STATUS_LABEL = { trial: "Testphase", active: "aktiv", cancelled: "Gekündigt" };

function ModuleAddonsDialog({ tenant, onClose }) {
  const [modules, setModules] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyModule, setBusyModule] = useState("");
  const [priceInputs, setPriceInputs] = useState({});
  const [freeGrantChecked, setFreeGrantChecked] = useState({});
  const [disableChecked, setDisableChecked] = useState({});

  const tid = tenant?.tenant_id;

  const loadAll = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    setError("");
    try {
      const res = await opsApi.moduleAddons();
      const mods = res?.modules || [];
      setModules(mods);
      const pairs = await Promise.all(
        mods.map((m) =>
          opsApi.moduleAddonStatus(tid, m.module_id).then((s) => [m.module_id, s])
        )
      );
      setStatuses(Object.fromEntries(pairs));
    } catch (err) {
      setError(opsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    if (tid) {
      setModules([]);
      setStatuses({});
      setPriceInputs({});
      setFreeGrantChecked({});
      setDisableChecked({});
      setError("");
      loadAll();
    }
  }, [tid, loadAll]);

  if (!tenant) return null;

  async function enableModule(moduleId) {
    setBusyModule(moduleId);
    try {
      await opsApi.moduleAddonEnable(tid, moduleId);
      toast.success("Modul aktiviert. Jetzt kann der Add-on-Preis gesetzt werden.");
      await loadAll();
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusyModule("");
    }
  }

  async function submitPrice(moduleId) {
    const cents = parseEuroToNetCents(priceInputs[moduleId]);
    if (cents == null) {
      toast.error("Ungültiger Betrag. Erwartet z. B. 4,90 (Euro, Komma, max. 2 Nachkommastellen).");
      return;
    }
    setBusyModule(moduleId);
    try {
      const res = await opsApi.moduleAddonSetPrice(tid, moduleId, cents);
      if (res?.price?.action === "price_set_zero") {
        toast.success("Preis 0 € gespeichert — es wird KEIN Abo angelegt.");
      } else {
        toast.success("Preis gesetzt und Add-on-Abo (14 Tage Testphase) angelegt.");
      }
      setPriceInputs((cur) => ({ ...cur, [moduleId]: "" }));
      await loadAll();
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusyModule("");
    }
  }

  // Kostenlos-Freigabe (autorisiert Feb 2026): materialize → Enable → 0€-Preis.
  // Läuft über die BESTEHENDEN Routen; bei Teil-Fehlschlag zeigt der Reload
  // den erreichten Zwischenzustand transparent an.
  async function freeGrant(moduleId) {
    setBusyModule(moduleId);
    try {
      await opsApi.contractMaterialize(tid);
      await opsApi.moduleAddonEnable(tid, moduleId);
      await opsApi.moduleAddonSetPrice(tid, moduleId, 0);
      toast.success(
        "Kostenlos freigegeben: Vertrag angelegt, Modul aktiviert, 0 € konfiguriert (kein Abo)."
      );
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusyModule("");
      await loadAll();
    }
  }

  // Deaktivierung (2026-06, F1-F8): feste Regel — Trial sofort, bezahltes Abo
  // zum Periodenende (Zugriff bleibt bis dahin), ohne Abo sofortige Entfernung.
  async function disableModule(moduleId) {
    setBusyModule(moduleId);
    try {
      const res = await opsApi.moduleAddonDisable(tid, moduleId);
      if (res?.action === "cancel_scheduled") {
        const datum = res.cancel_effective_at
          ? ` (${new Date(res.cancel_effective_at).toLocaleDateString("de-DE")})`
          : "";
        toast.success(
          `Abo gekündigt zum Periodenende${datum} — Zugriff bleibt bis dahin bestehen.`
        );
      } else if (res?.action === "cancel_already_scheduled") {
        toast.info("Kündigung ist bereits vorgemerkt — Zugriff endet zum Periodenende.");
      } else if (res?.action === "already_disabled") {
        toast.info("Modul war bereits deaktiviert.");
      } else {
        toast.success("Modul deaktiviert — Freischaltung entfernt.");
      }
      setDisableChecked((cur) => ({ ...cur, [moduleId]: false }));
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusyModule("");
      await loadAll();
    }
  }

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="ops-addons-dialog">
        <DialogHeader>
          <DialogTitle>Modul-Add-ons verwalten</DialogTitle>
          <DialogDescription>
            {tenant.company_name} · <span className="font-mono text-xs">{tid}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="ops-addons-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Add-on-Status wird geladen …
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600" data-testid="ops-addons-error">{error}</p>
        )}
        {!loading && !error && modules.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="ops-addons-empty">
            Keine Add-on-Module registriert.
          </p>
        )}

        <div className="space-y-4">
          {modules.map((m) => {
            const mid = m.module_id;
            const s = statuses[mid];
            if (!s) return null;
            const rowBusy = busyModule === mid;
            const hasSub = !!s.subscription_status;
            const zeroConfigured = s.amount_net_cents === 0 && !s.price_configured;
            return (
              <div
                key={mid}
                className="border border-slate-200 rounded-lg p-4 space-y-3"
                data-testid={`ops-addon-section-${mid}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <p className="font-medium text-slate-900">{s.product_name || mid}</p>
                    <p className="text-xs text-slate-400 font-mono">{mid}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    <Badge
                      variant="outline"
                      data-testid={`ops-addon-active-badge-${mid}`}
                      className={s.module_active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}
                    >
                      Modul: {s.module_active ? "aktiv" : "inaktiv"}
                      {/* MOD-01b-4 (Betreiberfreigabe 13.10., Soll 1): Quelle aus module_access. */}
                      {s.module_active && s.module_source ? (
                        <span data-testid={`ops-addon-source-${mid}`}> · {modulQuelleLabel(s.module_source)}</span>
                      ) : null}
                    </Badge>
                    <Badge
                      variant="outline"
                      data-testid={`ops-addon-price-badge-${mid}`}
                      className={s.price_configured ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}
                    >
                      Preis: {s.price_configured
                        ? `${fmtAddonEuro(s.amount_net_cents)}/Einheit·Monat`
                        : zeroConfigured
                        ? "0 € (kein Abo)"
                        : "nicht gesetzt"}
                    </Badge>
                    <Badge
                      variant="outline"
                      data-testid={`ops-addon-sub-badge-${mid}`}
                      className={hasSub ? "border-indigo-300 text-indigo-700" : "border-slate-300 text-slate-500"}
                    >
                      Abo: {hasSub ? (ADDON_SUB_STATUS_LABEL[s.subscription_status] || s.subscription_status) : "keins"}
                    </Badge>
                  </div>
                </div>

                <p className="text-xs text-slate-500" data-testid={`ops-addon-units-${mid}`}>
                  Abrechenbare Einheiten aktuell: <b>{s.billable_units}</b>
                </p>

                {s.has_contract && s.contract_resolvable === false && (
                  <p
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
                    data-testid={`ops-addon-contract-unresolvable-${mid}`}
                  >
                    Vertrag vorhanden, aber NICHT auflösbar (unknown_product /
                    invalid_contract_type). Aktivierung, Preis-Setzung und
                    Deaktivierung sind fail-closed blockiert, bis der Vertrag
                    wieder auflösbar ist.
                  </p>
                )}

                {!s.has_contract && (
                  <div className="space-y-2" data-testid={`ops-addon-no-contract-block-${mid}`}>
                    <p
                      className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
                      data-testid={`ops-addon-no-contract-${mid}`}
                    >
                      Kein Vertragsobjekt am Mandanten — die Modul-Aktivierung erfordert
                      einen bestehenden Vertrag (fail-closed). Demo-Mandanten ohne
                      Vertrag können hier nicht aktiviert werden.
                    </p>
                    <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 accent-slate-900"
                        checked={!!freeGrantChecked[mid]}
                        onChange={(e) =>
                          setFreeGrantChecked((cur) => ({ ...cur, [mid]: e.target.checked }))
                        }
                        data-testid={`ops-addon-free-grant-checkbox-${mid}`}
                      />
                      <span>
                        <b>Kostenlos freigeben (0 €, ohne Abo):</b> Legt EINMALIG ein
                        Standard-Vertragsobjekt aus dem Legacy-Mapping an, aktiviert das
                        Modul und speichert 0 € — kein Stripe-Objekt, kein Abo. Nicht
                        über die UI rückgängig zu machen.
                      </span>
                    </label>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`ops-addon-free-grant-submit-${mid}`}
                      disabled={rowBusy || !freeGrantChecked[mid]}
                      onClick={() => freeGrant(mid)}
                    >
                      {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kostenlos freigeben"}
                    </Button>
                  </div>
                )}

                {!s.module_active && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      size="sm"
                      data-testid={`ops-addon-enable-${mid}`}
                      disabled={rowBusy || !s.has_contract}
                      onClick={() => enableModule(mid)}
                    >
                      {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Modul aktivieren"}
                    </Button>
                    <span className="text-xs text-slate-500" data-testid={`ops-addon-price-locked-${mid}`}>
                      Preis-Setzung erst nach Modul-Aktivierung möglich.
                    </span>
                  </div>
                )}

                {s.module_active && hasSub && (
                  <div className="text-sm text-slate-700 space-y-1" data-testid={`ops-addon-sub-info-${mid}`}>
                    <p>
                      Laufendes Add-on-Abo · Status{" "}
                      <b>{ADDON_SUB_STATUS_LABEL[s.subscription_status] || s.subscription_status}</b>
                      {s.stripe_subscription_id && (
                        <>
                          {" "}· <span className="font-mono text-xs">{s.stripe_subscription_id}</span>
                        </>
                      )}
                    </p>
                    <p
                      className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-2"
                      data-testid={`ops-addon-running-hint-${mid}`}
                    >
                      Reiner Anzeige-Zustand: Eine Preisänderung an einem laufenden
                      Abo ist nicht Teil dieses Nachtrags.
                    </p>
                  </div>
                )}

                {s.module_active && !hasSub && (
                  <div className="space-y-2">
                    {zeroConfigured && (
                      <p className="text-xs text-slate-500" data-testid={`ops-addon-zero-hint-${mid}`}>
                        Aktuell 0 € konfiguriert (kein Abo). Ein neuer Betrag ersetzt die Konfiguration.
                      </p>
                    )}
                    <Label htmlFor={`addon-price-${mid}`}>
                      Add-on-Preis netto in € pro Einheit/Monat (z. B. 4,90 · 0 = kein Abo)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id={`addon-price-${mid}`}
                        data-testid={`ops-addon-price-input-${mid}`}
                        value={priceInputs[mid] || ""}
                        inputMode="decimal"
                        placeholder="z. B. 4,90"
                        className="max-w-[160px]"
                        onChange={(e) =>
                          setPriceInputs((cur) => ({ ...cur, [mid]: e.target.value }))
                        }
                      />
                      <Button
                        size="sm"
                        data-testid={`ops-addon-price-submit-${mid}`}
                        disabled={rowBusy || !(priceInputs[mid] || "").trim()}
                        onClick={() => submitPrice(mid)}
                      >
                        {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preis setzen + Abo anlegen"}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400">
                      Bei Betrag &gt; 0 wird atomar ein Add-on-Abo (14 Tage Testphase)
                      angelegt; 0 € speichert nur die Konfiguration (kein Abo).
                    </p>
                  </div>
                )}

                {s.module_active && s.product_bundled === false && (
                  s.subscription_status === "cancelled" ? (
                    <p
                      className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
                      data-testid={`ops-addon-cancel-scheduled-${mid}`}
                    >
                      Kündigung vorgemerkt
                      {s.cancel_effective_at
                        ? ` — Zugriff endet am ${new Date(s.cancel_effective_at).toLocaleDateString("de-DE")}`
                        : ""}
                      . Die Freischaltung wird nach dem Periodenende automatisch
                      entfernt (Lazy-Abgleich).
                    </p>
                  ) : (
                    <div
                      className="space-y-2 border-t border-slate-100 pt-3"
                      data-testid={`ops-addon-disable-block-${mid}`}
                    >
                      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-slate-900"
                          checked={!!disableChecked[mid]}
                          onChange={(e) =>
                            setDisableChecked((cur) => ({ ...cur, [mid]: e.target.checked }))
                          }
                          data-testid={`ops-addon-disable-checkbox-${mid}`}
                        />
                        <span>
                          <b>Modul deaktivieren:</b>{" "}
                          {hasSub
                            ? "Kündigt das laufende Abo (Testphase: sofort; bezahlt: zum Ende der bezahlten Periode — Zugriff bleibt bis dahin bestehen)."
                            : "Entfernt die Freischaltung sofort (kein Abo vorhanden)."}
                        </span>
                      </label>
                      <Button
                        size="sm"
                        variant="destructive"
                        data-testid={`ops-addon-disable-submit-${mid}`}
                        disabled={rowBusy || !disableChecked[mid]}
                        onClick={() => disableModule(mid)}
                      >
                        {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Modul deaktivieren"}
                      </Button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="ops-addons-refresh"
            disabled={loading || !!busyModule}
            onClick={loadAll}
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function OpsConsole() {
  const [authed, setAuthed] = useState(false);
  const [booting, setBooting] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  const [data, setData] = useState(null);
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [dlg, setDlg] = useState(null); // { type, tenant }
  const [resetText, setResetText] = useState("");
  const [extendDays, setExtendDays] = useState("14");
  const [busyTid, setBusyTid] = useState("");
  const [detailTid, setDetailTid] = useState(null); // P2.1 – geöffnete Mandanten-Detailansicht
  const [detailName, setDetailName] = useState("");
  const [rearchiveTenant, setRearchiveTenant] = useState(null); // ADR-007 B – Nacharchivierung
  const [addonTenant, setAddonTenant] = useState(null); // Block H Nachtrag – Modul-Add-ons

  // MOD-01b-4 (Betreiberfreigabe 13.10., Soll 1): Modulstatus (aktiv + Quelle) je Mandant aus
  // GET /ops/module-access – getrennt von tenant-metrics; fail-open („?") bei Fehler.
  const [moduleAccess, setModuleAccess] = useState({ status: "laedt", byTenant: {} });
  const loadModuleAccess = useCallback(async () => {
    setModuleAccess({ status: "laedt", byTenant: {} });
    try {
      if (typeof opsApi.moduleAccess !== "function") throw new Error("module-access nicht verfügbar");
      const res = await opsApi.moduleAccess();
      const byTenant = {};
      for (const row of res?.tenants || []) {
        if (row && row.tenant_id) byTenant[row.tenant_id] = row;
      }
      setModuleAccess({ status: "ok", byTenant });
    } catch {
      setModuleAccess({ status: "fehler", byTenant: {} });
    }
  }, []);

  // OPS-STATUS-FILTER (Feb 2026, betreibergenehmigt, Auftrag „Status-Kacheln
  // als Filter"): Rein clientseitige Filterung des bereits geladenen
  // data.tenants-Arrays. Zustände:
  //   "hide_suspended" (Default) – alle Status außer suspended sichtbar
  //   "active" | "demo" | "expired" | "suspended" – nur dieser Status
  //   "all" – alle Status inkl. suspended
  // Klick auf eine Status-Kachel toggelt in ihrem Bereich; Klick auf die
  // Gesamt-Kachel „Mandanten" toggelt zwischen „all" und Default (siehe
  // toggleStatusFilter / toggleTotalFilter in DashboardSection). Kein
  // Server-Filter, kein neuer Endpoint.
  const [statusFilter, setStatusFilter] = useState("hide_suspended");

  // OPS-TENANT-SEARCH (Feb 2026, betreibergenehmigt, Auftrag „Live-Suchleiste
  // über Mandantentabelle"): Rein clientseitige Substring-Suche über
  // `company_name`, `company_code`, `tenant_id` — case-insensitive. Kein
  // Debounce, kein Backend-Call. Kombination mit `statusFilter` erfolgt
  // per UND-Verknüpfung in `filteredTenants` (siehe unten). Leerer String
  // = keine Suche.
  const [searchQuery, setSearchQuery] = useState("");

  // OPS-DASHBOARD-SPLIT (2026-08-21, Betreiberauftrag Option A): Der
  // Dashboard-State (summary/loading/loadErr + Ladefunktion) wird EIN
  // MAL hier oben instanziiert — beide Sub-Komponenten
  // (`DashboardSectionTop`, `DashboardSectionBottom`) konsumieren dasselbe
  // `dash`-Objekt via Prop. Das garantiert genau einen HTTP-Aufruf gegen
  // `/api/ops/dashboard/summary` pro Refresh (kein Doppel-Load, auch
  // nicht zwischen den beiden Cards um die Mandantentabelle herum).
  // `enabled: authed` bewahrt die OPS-504-FIX-Auth-Isolation: solange
  // der Nutzer die Token-Maske sieht, wird der Endpunkt nicht getroffen.
  const dash = useDashboardSummary({ enabled: authed });

  // OPS-504-FIX (2026-08, Betreiberauftrag Teil C): Auth ausschließlich über
  // den schnellen Ops-Auth-Check (keine DB). tenant-metrics ist reiner
  // Datenendpunkt — sein Erfolg/Fehler entscheidet NICHT mehr über `authed`.
  const checkAuth = useCallback(async () => {
    try {
      await opsApi.authCheck();
      setAuthed(true);
      setAuthError("");
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403 || status === 503) {
        clearOpsToken();
      }
      setAuthed(false);
      setData(null);
      setAuthError(opsErrorMessage(err));
      return false;
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setLoadLoading(true);
    setLoadError("");
    try {
      const d = await opsApi.tenantMetrics();
      setData(d);
      // MOD-01b-4 (Betreiberfreigabe 13.10., Soll 1): Modulstatus nachträglich und getrennt laden –
      // die Liste bleibt bedienbar, auch wenn dieser Endpunkt fehlschlägt.
      loadModuleAccess();
      return true;
    } catch (err) {
      // OPS-504-FIX: JEDER tenant-metrics-Fehler (auch 504/Timeout) bleibt im
      // Metrikbereich isoliert — keine Rückkehr zur Token-Maske, kein authed-
      // Wechsel; bereits geladene Ops-Bereiche bleiben sichtbar.
      setLoadError(opsErrorMessage(err));
      return false;
    } finally {
      setLoadLoading(false);
    }
  }, [loadModuleAccess]);

  useEffect(() => {
    if (getOpsToken()) {
      checkAuth()
        .then((ok) => {
          // Metriken laden bewusst OHNE await: die Konsole öffnet sofort,
          // der Metrikbereich zeigt seinen eigenen Ladezustand.
          if (ok) loadMetrics();
        })
        .finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
  }, [checkAuth, loadMetrics]);

  async function handleTokenSubmit(token) {
    const t = (token || "").trim();
    if (!t || authBusy) return;
    setAuthBusy(true);
    setAuthError("");
    setOpsToken(t);
    const ok = await checkAuth();
    if (ok) loadMetrics();
    setAuthBusy(false);
  }

  function lock() {
    clearOpsToken();
    setAuthed(false);
    setData(null);
    setAuthError("");
  }

  function openDialog(type, tenant) {
    setResetText("");
    setExtendDays("14");
    setDlg({ type, tenant });
  }

  async function runAction(type, tenant, extra) {
    if (busyTid) return; // keine parallelen/doppelten Aktionen
    setBusyTid(tenant.tenant_id);
    try {
      if (type === "extend") await opsApi.extend(tenant.tenant_id, Number(extra.days));
      else if (type === "suspend") await opsApi.suspend(tenant.tenant_id);
      else if (type === "activate") await opsApi.activate(tenant.tenant_id);
      else if (type === "upgrade") await opsApi.upgrade(tenant.tenant_id);
      else if (type === "reset") await opsApi.factoryReset(tenant.tenant_id);
      toast.success(`${ACTION_META[type]?.label || "Aktion"}: ${tenant.company_name} aktualisiert.`);
      await loadMetrics();
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusyTid("");
    }
  }

  function confirmDialog() {
    const d = dlg;
    setDlg(null);
    if (!d) return;
    if (d.type === "extend") runAction("extend", d.tenant, { days: extendDays });
    else runAction(d.type, d.tenant);
  }

  // --- Render: Boot / Token-Maske / Konsole --------------------------------

  if (booting) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="ops-booting">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!authed) {
    return <TokenGate onSubmit={handleTokenSubmit} error={authError} busy={authBusy} />;
  }

  // OPS-KPI-CLEANUP (2026-08-21, Betreiberauftrag Option 1): Die duplizierte
  // untere KPI-Reihe (KpiCard × 7) direkt vor der Mandantentabelle wurde
  // entfernt (Redundanz zur oberen DashKpi-Reihe im Dashboard-Bereich,
  // seit Filter-Aktivierung obendrein potenziell irreführend, weil statisch).
  // `totals` wird dadurch nicht mehr in dieser Komponente gebraucht;
  // Aggregatwerte kommen weiterhin über `DashboardSection`/`dashboardSummary`.
  const tenants = data?.tenants || [];

  // OPS-STATUS-FILTER (Feb 2026): angezeigte Menge + Anzahl der aktuell
  // ausgeblendeten Gesperrten (nur relevant im Default-Zustand
  // "hide_suspended", damit der Hinweistext sichtbar wird). Andere Filter-
  // Werte blenden gesperrte nicht implizit aus, sondern zeigen den
  // gewählten Status exklusiv — dann ist kein Hinweistext nötig.
  // OPS-TENANT-SEARCH (Feb 2026): zusätzlicher UND-verknüpfter Substring-
  // Filter über `company_name`, `company_code`, `tenant_id` — wird nur
  // angewendet, wenn `searchQuery.trim()` nicht leer ist. Kein Debounce,
  // kein Ersatz für Status-Filter — beide wirken additiv.
  const searchNeedle = searchQuery.trim().toLowerCase();
  const filteredTenants = tenants.filter((row) => {
    // 1) Status-Filter (unverändert)
    let statusOk;
    if (statusFilter === "all") statusOk = true;
    else if (statusFilter === "hide_suspended") statusOk = row.account_status !== "suspended";
    else statusOk = row.account_status === statusFilter;
    if (!statusOk) return false;
    // 2) Such-Filter (additiv, case-insensitive Substring)
    if (!searchNeedle) return true;
    const hay = [
      row.company_name,
      row.company_code,
      row.tenant_id,
    ]
      .map((v) => (v == null ? "" : String(v).toLowerCase()))
      .join(" \u241f ");
    return hay.includes(searchNeedle);
  });
  const suspendedHiddenCount =
    statusFilter === "hide_suspended"
      ? tenants.filter((row) => row.account_status === "suspended").length
      : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ops-console">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6" />
            <div>
              <h1 className="font-heading text-xl font-bold leading-tight">Betreiber-Konsole</h1>
              <p className="text-xs text-slate-300">Mandantenverwaltung · intern</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ops-refresh-button"
              variant="outline"
              onClick={() => loadMetrics()}
              disabled={loadLoading}
              className="bg-transparent border-slate-600 text-white hover:bg-slate-800"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadLoading ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
            <Button
              data-testid="ops-lock-button"
              variant="outline"
              onClick={lock}
              className="bg-transparent border-slate-600 text-white hover:bg-slate-800"
            >
              <Lock className="w-4 h-4 mr-2" />
              Sperren
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <DashboardSectionTop dash={dash} statusFilter={statusFilter} onFilterChange={setStatusFilter} />

        {/* OPS-504-FIX: Metrikbereich isoliert — eigener Ladezustand, KPIs nur
           bei vorhandenen Daten (keine irreführenden 0-Werte bei Fehler). */}
        {loadLoading && !data && (
          <Card className="bg-white border border-slate-200 p-6" data-testid="ops-metrics-loading">
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Mandanten-Kennzahlen werden geladen …
            </p>
          </Card>
        )}

        {/* OPS-KPI-CLEANUP (2026-08-21, Betreiberauftrag Option 1):
            Die zuvor hier platzierte duplizierte KPI-Reihe (KpiCard × 7 —
            Mandanten/Aktiv/Demo/Abgelaufen/Gesperrt/Läuft <7 Tage ab/
            Potenzial MRR) wurde ersatzlos entfernt. Sie war deckungsgleich
            mit den ersten sechs DashKpi-Kacheln im Dashboard-Bereich oben
            und blieb — im Gegensatz zu jenen — statisch und ohne Filter-
            Reaktion. Aggregatwerte werden weiterhin von `DashboardSection`
            (obere Reihe, klickbar) geliefert. Die `KpiCard`-Komponente
            selbst bleibt unverändert (weiterhin in AI-Usage, Übersetzungs-,
            Usage- und Kosten-Overview eingesetzt). */}

        <div id="ops-section-tenants" className="flex items-center justify-between scroll-mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-heading text-lg font-bold text-slate-900">
              Mandanten ({data ? filteredTenants.length : "–"}
              {data && filteredTenants.length !== tenants.length
                ? ` von ${tenants.length}`
                : ""}
              )
            </h2>
            {suspendedHiddenCount > 0 && (
              <p
                className="text-xs text-slate-500"
                data-testid="ops-status-filter-hint"
              >
                {suspendedHiddenCount} gesperrte Mandanten ausgeblendet — Kachel „Gesperrt" anklicken zum Anzeigen, „Mandanten" für alle
              </p>
            )}
          </div>
          <Button
            data-testid="ops-create-open-button"
            onClick={() => setCreateOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Demo-Mandant anlegen
          </Button>
        </div>
        {/* OPS-TENANT-SEARCH (Feb 2026): Live-Substring-Suche über
            company_name/company_code/tenant_id — reine Client-Filterung. */}
        <div className="flex items-center gap-2 -mt-2">
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Suche nach Firma, Code oder Tenant-ID…"
            className="max-w-md h-9 text-sm"
            data-testid="ops-tenant-search"
          />
          {searchQuery && (
            <button
              type="button"
              data-testid="ops-tenant-search-clear"
              onClick={() => setSearchQuery("")}
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            >
              Suche leeren
            </button>
          )}
        </div>

        {loadError && (
          <Card className="border border-red-200 bg-red-50 p-4" data-testid="ops-load-error">
            <div className="flex items-center justify-between">
              <p className="text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Mandanten-Kennzahlen konnten nicht geladen werden: {loadError}
              </p>
              <Button
                data-testid="ops-retry-button"
                variant="outline"
                onClick={() => loadMetrics()}
                disabled={loadLoading}
              >
                Erneut versuchen
              </Button>
            </div>
          </Card>
        )}

        <Card className="bg-white border border-slate-200 overflow-hidden">
          {/* OPS-STICKY-HEADER (Feb 2026, betreibergenehmigt):
              Sticky an <th> bindet an den nächsten scrollenden Vorfahren.
              Der äußere `overflow-x-auto`-Wrapper (nur horizontal) UND der
              interne ui/table.jsx-Wrapper (`overflow-auto`) sind BEIDE
              scroll-containment-Kandidaten ohne feste Höhe → sticky klebt
              nirgends. Fix in EINER Stelle (kein globales Refactor an
              ui/table.jsx): dieser Wrapper wird zum eigenen vertikalen
              Scroll-Container mit `max-h-[75vh]`, und `[&>div]:overflow-visible`
              öffnet den inneren ui/table.jsx-Wrapper (bricht das
              containment-Kettenglied auf, ohne die Basis-Komponente
              anzufassen — andere Nutzer der Table bleiben unberührt). */}
          <div className="relative max-h-[75vh] overflow-auto [&>div]:overflow-visible">
            <Table data-testid="ops-tenant-table">
              {/* OPS-STICKY-HEADER (Feb 2026, betreibergenehmigt): Bei 65
                  Mandanten × variabler Zeilenhöhe (Aktionen-Spalte umbrechend)
                  verliert man beim vertikalen Seiten-Scroll die Spalten-
                  zuordnung. Sticky auf jedem <th> (statt <thead>/<tr>), weil
                  das browserübergreifend robuster ist (insb. iPad-Safari).
                  Bindet an den nächst-höheren Scroll-Ancestor = Browser-
                  Viewport (die Seite scrollt vertikal, der Card-Wrapper hat
                  keine feste Höhe). `bg-white` deckt durchscheinende
                  Zeileninhalte ab; `z-10` bleibt unter Dialog-Overlays. */}
              <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-white">
                <TableRow>
                  <TableHead>Firma</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tarif</TableHead>
                  <TableHead>Frist</TableHead>
                  <TableHead className="text-right">Tage</TableHead>
                  <TableHead>Setup</TableHead>
                  <TableHead className="text-right">MA</TableHead>
                  <TableHead className="text-right">Ausl.</TableHead>
                  <TableHead className="text-right">MRR</TableHead>
                  <TableHead className="text-right">Welten</TableHead>
                  <TableHead className="text-right">Fahrer</TableHead>
                  <TableHead>Aushang</TableHead>
                  <TableHead className="text-right">KI-W.</TableHead>
                  <TableHead className="text-right">Zuw.</TableHead>
                  <TableHead className="text-right">Nachw.</TableHead>
                  <TableHead className="text-right">PDF ⏳</TableHead>
                  <TableHead className="text-right">PDF ⚠</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.length === 0 && !loadLoading && !loadError && (
                  <TableRow>
                    <TableCell colSpan={18} className="text-center text-slate-400 py-8" data-testid="ops-empty">
                      {tenants.length === 0
                        ? "Keine Mandanten vorhanden."
                        : searchNeedle
                          ? "Keine Mandanten für die aktuelle Suche/Filter-Kombination."
                          : "Keine Mandanten für den gewählten Filter."}
                    </TableCell>
                  </TableRow>
                )}
                {filteredTenants.map((t) => {
                  const rowBusy = busyTid === t.tenant_id;
                  const daysCls =
                    t.days_until_expiry == null
                      ? "text-slate-400"
                      : t.days_until_expiry < 0
                      ? "text-red-600 font-semibold"
                      : t.days_until_expiry <= 7
                      ? "text-orange-600 font-semibold"
                      : "text-slate-700";
                  return (
                    <TableRow key={t.tenant_id} data-testid={`ops-tenant-row-${t.tenant_id}`}>
                      <TableCell>
                        <button
                          type="button"
                          data-testid={`ops-tenant-open-${t.tenant_id}`}
                          onClick={() => {
                            setDetailName(t.company_name || "");
                            setDetailTid(t.tenant_id);
                          }}
                          className="text-left font-medium text-slate-900 hover:text-indigo-700 hover:underline focus:outline-none focus:underline"
                          title="Detailansicht öffnen"
                        >
                          {t.company_name || "–"}
                        </button>
                        <div className="text-xs text-slate-400">
                          {t.company_code || "–"} · <span title={t.tenant_id}>{shortId(t.tenant_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={t.account_status} /></TableCell>
                      <TableCell className="text-sm" data-testid={`ops-plan-${t.tenant_id}`}>
                        <div className="font-medium text-slate-800">{t.plan_name || "–"}</div>
                        {(t.billing_interval === "yearly" || t.billing_interval === "monthly") && (
                          <div className="text-xs text-slate-400">
                            {t.billing_interval === "yearly" ? "jährlich" : "monatlich"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDate(t.trial_ends_at)}</TableCell>
                      <TableCell className={`text-right text-sm ${daysCls}`}>
                        {t.days_until_expiry == null ? "–" : t.days_until_expiry}
                      </TableCell>
                      <TableCell>
                        {t.setup_completed ? (
                          <span className="text-emerald-600 text-sm">✓</span>
                        ) : (
                          <span className="text-slate-400 text-sm">–</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">{t.employee_count ?? 0}</TableCell>
                      <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`ops-usage-${t.tenant_id}`}>
                        {t.active_employee_count ?? 0}/{t.max_employees == null ? "∞" : t.max_employees}
                        {t.usage_percent != null && (
                          <span
                            className={`ml-1 text-xs ${
                              t.usage_percent >= 100
                                ? "text-red-600 font-semibold"
                                : t.usage_percent >= 80
                                ? "text-orange-600"
                                : "text-slate-400"
                            }`}
                          >
                            ({t.usage_percent}%)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`ops-mrr-${t.tenant_id}`}>
                        {fmtEuro(t.estimated_mrr_net_cents || t.potential_mrr_net_cents || 0)}
                      </TableCell>
                      <TableCell className="text-right text-sm">{t.world_count ?? 0}</TableCell>
                      <TableCell
                        className="text-right text-sm"
                        data-testid={`ops-drivers-${t.tenant_id}`}
                      >
                        {t.active_driver_count ?? 0}
                      </TableCell>
                      <TableCell data-testid={`ops-aushang-${t.tenant_id}`}>
                        {/* MOD-01b-4 (Betreiberfreigabe 13.10., Soll 1): Status + Quelle aus module_access
                            (GET /ops/module-access), nicht mehr aus t.aushang_active (Alt-Logik). */}
                        <ModulStatusZelle moduleAccess={moduleAccess} tenantId={t.tenant_id} moduleId="aushang_addon" />
                      </TableCell>
                      <TableCell className="text-right text-sm">{t.ai_generated_world_count ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{t.assignment_count ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{t.nachweis_count ?? 0}</TableCell>
                      <TableCell className="text-right text-sm">{t.pdf_outbox_pending_count ?? 0}</TableCell>
                      <TableCell className={`text-right text-sm ${(t.pdf_outbox_error_count ?? 0) > 0 ? "text-red-600 font-semibold" : ""}`}>
                        {t.pdf_outbox_error_count ?? 0}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <Button
                            size="sm" variant="outline"
                            data-testid={`ops-action-extend-${t.tenant_id}`}
                            disabled={rowBusy}
                            onClick={() => openDialog("extend", t)}
                          >
                            Verlängern
                          </Button>
                          {t.account_status !== "active" && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`ops-action-activate-${t.tenant_id}`}
                              disabled={rowBusy}
                              onClick={() => runAction("activate", t)}
                            >
                              Aktivieren
                            </Button>
                          )}
                          {t.account_status !== "suspended" && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`ops-action-suspend-${t.tenant_id}`}
                              disabled={rowBusy}
                              onClick={() => openDialog("suspend", t)}
                            >
                              Sperren
                            </Button>
                          )}
                          {t.account_status !== "active" && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`ops-action-upgrade-${t.tenant_id}`}
                              disabled={rowBusy}
                              onClick={() => openDialog("upgrade", t)}
                            >
                              Upgrade
                            </Button>
                          )}
                          {t.account_status === "demo" && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`ops-action-reset-${t.tenant_id}`}
                              disabled={rowBusy}
                              onClick={() => openDialog("reset", t)}
                              className="border-red-200 text-red-700 hover:bg-red-50"
                            >
                              Werksreset
                            </Button>
                          )}
                          {/^(demo-|test-|qa-)/.test(t.tenant_id || "") && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`ops-action-rearchive-${t.tenant_id}`}
                              disabled={rowBusy}
                              onClick={() => setRearchiveTenant(t)}
                            >
                              Nacharchivierung
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline"
                            data-testid={`ops-action-addons-${t.tenant_id}`}
                            disabled={rowBusy}
                            onClick={() => setAddonTenant(t)}
                          >
                            Add-ons
                          </Button>
                          {rowBusy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* OPS-DASHBOARD-SPLIT (2026-08-21, Betreiberauftrag Option A):
            Untere Hälfte der zuvor monolithischen Dashboard-Card, direkt
            hinter der Mandantentabelle platziert. Konsumiert dasselbe
            `dash`-Objekt wie die obere Card → gemeinsamer State, ein
            einziger HTTP-Aufruf pro Refresh. */}
        <DashboardSectionBottom dash={dash} />

        {/* DEPLOY-01 (Betreiberfreigabe 14.10., R3): Systemstart – Startstatus des laufenden
            Backend-Prozesses (Schritte, Startzeitpunkt, übersprungene Altdatensätze), nur lesend. */}
        <div id="ops-section-systemstart" className="scroll-mt-6">
          <OpsSystemstartSection />
        </div>

        {/* ZAHL-02e (Betreiberfreigabe 14.10., E1): Offene Vorgänge – mandantenübergreifend, nur lesend;
            Zeilen verlinken auf das Mandanten-Detail (Abschnitte „Vertrag"/„Abrechnung"). */}
        <div id="ops-section-offene-vorgaenge" className="scroll-mt-6">
          <OpsOffeneVorgaengeSection
            onOpenTenant={(tid, name) => {
              setDetailName(name || "");
              setDetailTid(tid);
            }}
          />
        </div>

        <PlanChangeRequestsSection />

        <div id="ops-section-aushang" className="scroll-mt-6">
          <OpsAushangSection />
        </div>

        <div id="ops-section-aushang-quellen" className="scroll-mt-6">
          <OpsAushangQuellenV2Section />
        </div>

        <div id="ops-section-translations" className="scroll-mt-6">
          <TranslationCenterSection />
        </div>

        <div id="ops-section-usage" className="scroll-mt-6">
          <UsageCenterSection />
        </div>

        <div id="ops-section-costs" className="scroll-mt-6">
          <CostCenterSection />
        </div>

        <div id="ops-section-prices" className="scroll-mt-6">
          <PriceEditorSection />
        </div>

        <div id="ops-section-shadow-validation" className="scroll-mt-6">
          <ShadowValidationSection />
        </div>

        <div id="ops-section-contract-preview" className="scroll-mt-6">
          <OpsContractPreviewSection />
        </div>

        <div id="ops-section-sales-catalog" className="scroll-mt-6">
          <SalesCatalogSection />
        </div>

        <p className="text-xs text-slate-400 flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5" />
          Alle Aktionen werden serverseitig (ops_audit) protokolliert. Diese Konsole
          ist reiner Komfort – das Backend bleibt maßgeblich.
        </p>
      </main>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={loadMetrics} />
      <RearchiveDialog tenant={rearchiveTenant} onClose={() => setRearchiveTenant(null)} />
      <ModuleAddonsDialog tenant={addonTenant} onClose={() => setAddonTenant(null)} />
      <TenantDetailDialog
        tenantId={detailTid}
        fallbackName={detailName}
        onClose={() => setDetailTid(null)}
      />
      <ConfirmDialog
        dlg={dlg}
        onClose={() => setDlg(null)}
        onConfirm={confirmDialog}
        busy={!!busyTid}
        resetText={resetText}
        setResetText={setResetText}
        extendDays={extendDays}
        setExtendDays={setExtendDays}
      />
    </div>
  );
}
